// 회의록 상세 — 관리(STT 사용량·감사로그) 뷰 + 노트 상세(헤더·재생바·요약·전사 보기/편집).
// 순수 프레젠테이션: 상태·핸들러·ref는 MeetingsPage에서 props로 주입(동작 100% 동일).
// 스크롤싱크/오디오로드 effect·playIdx는 부모(MeetingsPage)가 유지, 여기선 id=mseg-i 렌더만.
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkCjkStrong from '../../utils/remarkCjkStrong';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import Markdown from '../../components/Markdown';
import {
  IconDotsVertical,
  IconBook,
  IconBrain,
  IconDownload,
  IconFileText,
  IconFolder,
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconReplace,
  IconSearch,
  IconStack2,
  IconTrash,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import {
  CANCELLABLE_STT_STATUSES,
  MEETING_THEME,
  STATUS,
  ACTIONS,
  fmtDate,
  fmtDur,
  mdHardBreaks,
  procInfo,
  diarInfo,
  dispSpk,
  speakerAvatar,
  smBtn,
  thAdmin,
  tdAdmin,
  softWrap,
} from '../../utils/meetingUtil';
import { hasSummaryOrNotes } from './summaryNotesText';
import { speakerShare } from './speakerShare';
import WordReplaceDialog from './WordReplaceDialog';
import GlossaryDialog from './GlossaryDialog';
import { detailOr, jsonFetch } from './meetingWordsApi';
import { wordSpans, activeWordIndex } from './wordTiming';
import { pathText } from '../../utils/meetingFolderTree';
import { scaleOf } from '../../lib/floatingBounds';
import { TABBAR_H, tabbarCalc } from '../../lib/tabbarMetrics';

const T = MEETING_THEME;

const PARTICIPANT_NAME_MAX = 40;   // 서버 PARTICIPANT_NAME_MAX
const PARTICIPANTS_MAX = 30;       // 서버 PARTICIPANTS_MAX
const HIDE_SPEAKERS_KEY = 'gwMeetingHideSpeakers';   // 보는 사람 브라우저에만 남는 표시 설정

// 요약 버전 칩 — 서버 `_summary_prompt_label`('단어 바꾸기: …'·'단어 바꾸기 전')을 칩 폭에 맞게 줄인 말.
// 🔴 `summaryModes` 에 넣으면 «요약 양식» 탭으로 새 나가므로 칩 전용 보조 표로 둔다.
const SUMMARY_VERSION_MODE_LABELS = { replace: '단어 바꾸기', original: '바꾸기 전' };

const RECORDING_NOTES_MAX = 200;   // 서버 _normalize_recording_notes 상한
const RECORDING_NOTE_TEXT_MAX = 1000;   // 서버가 1000자로 자른다
const SEG_MENU_GAP = 4;            // 발화 ⋮ 펼침 상자와 화면 끝 사이 여유
const SEG_MENU_MIN_H = 120;        // 아주 좁은 화면에서도 스크롤로 쓸 수 있는 최소 높이

const REPLACE_WORD_MAX = 100;      // 서버 REPLACE_WORD_MAX — 검색어를 찾을 단어로 넘길 때도 같은 상한
// ⋮ «단어 바꾸기» 로 열 때의 시작값 — 빈 칸 + 대상 셋 다(모듈 상수라 상태 비교가 안정적이다).
const REPLACE_INIT_ALL = { find: '', targets: { transcript: true, summary: true, notes: true } };

// 머리 «모아보기»(클로바 묶음 D2a) — 오른쪽 패널을 표식 모음으로 바꾼다. 「링크」 탭은 뺀다(마스터 결정).
// 🔸 발화 도구 버튼 글자도 이 판과 같은 «하이라이트» 다(D2c 에서 통일했다 — 데이터 키·testid 는 highlight).
const COLLECT_TABS = [
  { key: 'bookmark', label: '북마크', empty: '저장된 북마크가 없어요. 중요한 음성 기록 구간에 북마크해 보세요.' },
  { key: 'highlight', label: '하이라이트', empty: '저장된 하이라이트가 없어요. 중요한 음성 기록 구간에 하이라이트해 보세요.' },
];
const COLLECT_GAP = 8;             // sticky 머리·화면 끝과 모아보기 판 사이 여유
const COLLECT_MAIN_PAD = 24;       // <main> 아래 여백
const COLLECT_PLAYBAR_H = 86;      // 재생바가 있을 때 상세 상자가 더 두는 아래 여백

// 키보드 포커스(:focus-visible)일 때만 발화 도구를 띄운다 — 마우스로 발화를 눌러 재생할 때
// 포커스가 남아 도구가 계속 떠 있던 문제를 막는다. matches 를 못 쓰는 환경이면 끈다.
const isKeyboardFocus = (el) => {
  try {
    return !!el && typeof el.matches === 'function' && el.matches(':focus-visible');
  } catch (e) {
    return false;
  }
};

const fmtSummaryVersionDate = (s) => {
  if (!s) return '';
  const raw = String(s);
  const d = new Date(raw.endsWith('Z') ? raw : `${raw}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const mdComponents = {
  p({ children, ...props }) {
    return <p {...props} style={{ margin: '0 0 10px', fontSize: '14px', lineHeight: 1.65, color: '#32312e', ...softWrap }}>{children}</p>;
  },
  h1({ children, ...props }) {
    return <h1 {...props} style={{ fontSize: '17px', fontWeight: 800, margin: '2px 0 10px', lineHeight: 1.35, color: '#32312e' }}>{children}</h1>;
  },
  h2({ children, ...props }) {
    return <h2 {...props} style={{ fontSize: '15px', fontWeight: 800, margin: '16px 0 8px', lineHeight: 1.35, color: '#32312e' }}>{children}</h2>;
  },
  h3({ children, ...props }) {
    return <h3 {...props} style={{ fontSize: '14px', fontWeight: 800, margin: '14px 0 6px', lineHeight: 1.35, color: '#32312e' }}>{children}</h3>;
  },
  ul({ children, ...props }) {
    return <ul {...props} style={{ margin: '0 0 12px', paddingLeft: '20px', fontSize: '14px', lineHeight: 1.6, color: '#32312e' }}>{children}</ul>;
  },
  ol({ children, ...props }) {
    return <ol {...props} style={{ margin: '0 0 12px', paddingLeft: '20px', fontSize: '14px', lineHeight: 1.6, color: '#32312e' }}>{children}</ol>;
  },
  li({ children, ...props }) {
    return <li {...props} style={{ margin: '4px 0', ...softWrap }}>{children}</li>;
  },
  code({ children, ...props }) {
    return <code {...props} style={{ background: '#f1f1ef', borderRadius: '4px', padding: '1px 4px', fontSize: '12px' }}>{children}</code>;
  },
};

const renderTranscriptSearchText = (text, query) => {
  const raw = String(text ?? '');
  const needle = (query || '').trim();
  if (!needle) return raw;

  const lowerRaw = raw.toLocaleLowerCase();
  const lowerNeedle = needle.toLocaleLowerCase();
  const parts = [];
  let cursor = 0;
  let hit = lowerRaw.indexOf(lowerNeedle);

  while (hit !== -1) {
    if (hit > cursor) parts.push(raw.slice(cursor, hit));
    const end = hit + needle.length;
    parts.push(
      <mark key={`hit-${hit}-${parts.length}`} style={{ background: '#fde68a', color: 'inherit', padding: '0 2px', borderRadius: '3px' }}>
        {raw.slice(hit, end)}
      </mark>,
    );
    cursor = end;
    hit = lowerRaw.indexOf(lowerNeedle, cursor);
  }

  if (cursor < raw.length) parts.push(raw.slice(cursor));
  return parts.length > 0 ? parts : raw;
};

// 재생 위치가 든 발화의 «지금 말하는 낱말» 만 감싼다(S4). 낱말 시각(words)이 없거나 · 글자가 안 맞거나 ·
// 첫 낱말 전이면 null — 부르는 쪽이 지금 값을 그대로 쓴다. 🔴 `<mark>` 는 검색 칠하기 몫이라 쓰지 않는다.
const renderPlayingWordText = (text, words, sec) => {
  const spans = wordSpans(text, words);
  const k = activeWordIndex(spans, sec);
  if (k < 0) return null;
  const { from, to } = spans[k];
  const parts = [];
  if (from > 0) parts.push(text.slice(0, from));
  parts.push(
    <span key="w" data-testid="mword-active" style={{ background: '#fbbf24', borderRadius: '3px' }}>
      {text.slice(from, to)}
    </span>,
  );
  if (to < text.length) parts.push(text.slice(to));
  return parts;
};

const findFolderById = (folders, folderId) => {
  if (folderId === null || folderId === undefined || folderId === '') return null;
  return (Array.isArray(folders) ? folders : []).find((folder) => String(folder.id) === String(folderId)) || null;
};

const folderSummaryPromptNotice = (meeting, folders) => {
  if (!meeting || meeting.folder_id === null || meeting.folder_id === undefined || meeting.folder_id === '') {
    return {
      text: '⚠ 이 노트는 폴더에 속해 있지 않아 기본 양식으로 생성됩니다.',
      title: '',
      warning: true,
    };
  }

  const folder = findFolderById(folders, meeting.folder_id);
  const prompt = String(folder?.summary_prompt || '').trim();
  if (!prompt) {
    return {
      text: '⚠ 이 폴더에 지정된 양식이 없습니다 — 기본 양식으로 생성됩니다.',
      title: '',
      warning: true,
    };
  }

  const firstLine = prompt.split(/\r?\n/)[0].trim();
  const previewSource = firstLine || prompt;
  const preview = previewSource.length > 60 ? previewSource.slice(0, 60) : previewSource;
  const needsEllipsis = previewSource.length > 60 || prompt.includes('\n') || prompt.includes('\r');
  return {
    text: `이 폴더 양식 사용: ${preview}${needsEllipsis ? '…' : ''}`,
    title: prompt,
    warning: false,
  };
};

export default function MeetingDetail({
  active, adminView, setActive, setAdminView, mobile, usage,
  auditLog, renameMeeting, startStt, cancelStt, sttStarting, dlAudio, dlTxt, dlSummary, dlSummaryNotes,
  setMoveOpen, delMeeting, audioUrl, audioError = null, retryAudio, audioRef, setPlaySec, nowMs,
  queueText, sumMode, setSumMode, genSummary, summarizing, customPrompt,
  setCustomPrompt, editing, startEdit, saveEdit, saving, setEditing,
  seekTo, playSec, spkDisp, spkMap, setSpkMap, editSegs,
  setEditSegs, markSaving, toggleSegFlag,
  editSegMemo,
  saveRecordingNotes, notesSaving = false,
  setRagOpt,
  requestRediarize, rediarizing,
  folders = [],
}) {
  // 음성 기록 ↔ 메모·요약 폭 조절(마스터 지시 2026-08-12).
  // 평소엔 구분선이 보이지 않고, 2열 영역에 마우스가 들어오면 회색, 핸들 위에 올리거나
  // 드래그 중이면 파랑으로 바뀐다.
  const NOTES_MIN = 300;        // 메모·요약 최소 폭
  const NOTES_MAX_RATIO = 0.55; // 메모·요약이 가져갈 수 있는 최대 비율
  const TRANSCRIPT_MIN = 420;   // 음성 기록이 확보해야 할 최소 폭
  const NOTES_DEFAULT = 420;
  const SPLIT_GAP = 16;         // 두 열 사이 구분선 자리(옛 gap) — 폭 계산에서 빼야 음성 기록이 420 을 지킨다
  // 두 열이 들어갈 수 있는 최소 상자 폭 = 300 + 16 + 420 = 736. 이보다 좁으면 두 열이 원리상 불가하다.
  const SPLIT_MIN = NOTES_MIN + SPLIT_GAP + TRANSCRIPT_MIN;
  // 🔴 구분선 16 을 반드시 뺀다 — 안 빼면 제한을 걸어도 음성 기록이 420 이 아니라 404 가 된다.
  const notesMaxFor = (w) => Math.max(NOTES_MIN, Math.min(w - SPLIT_GAP - TRANSCRIPT_MIN, w * NOTES_MAX_RATIO));
  const splitRef = useRef(null);
  const splitRoRef = useRef(null);
  const sepRef = useRef(null);
  const draggingRef = useRef(false);
  const [notesW, setNotesW] = useState(() => {
    try {
      const saved = Number(window.localStorage.getItem('gwMeetingNotesW'));
      if (Number.isFinite(saved) && saved >= NOTES_MIN) return saved;
    } catch (e) { /* 저장소 접근 불가 시 기본값 */ }
    return NOTES_DEFAULT;
  });
  // 분할 상자의 실제 폭. 화면 폭만으로는 사이드바·목록 칸을 뺄 수 없어 상자를 직접 잰다.
  const [splitW, setSplitW] = useState(0);
  const [splitAreaHover, setSplitAreaHover] = useState(false);
  const [splitHandleHot, setSplitHandleHot] = useState(false);

  // 분할 상자 ref — 붙는 즉시 폭을 재고, ResizeObserver 로 그 뒤 변화(창 크기·사이드바 접힘)도 따라간다.
  // 🔴 콜백 ref 로 둔다 — effect 로 재면 처음 그릴 때의 한 프레임이 제한 없는 폭으로 나간다.
  // 🔴 ResizeObserver 가 없거나(jsdom) 폭이 0 이면 splitW=0 → 옛 동작(두 열 · notesW 그대로)을 지킨다.
  const setSplitNode = useCallback((node) => {
    splitRef.current = node;
    if (splitRoRef.current) {
      splitRoRef.current.disconnect();
      splitRoRef.current = null;
    }
    if (!node) {
      setSplitW(0);
      return;
    }
    // 웹 배율(<html> zoom)이 걸리면 rect 는 화면 px, 폭 style 은 CSS px 다 → 상자의 배율로 나눠 CSS px 로 든다(jsdom·1.0 은 1).
    const measure = () => {
      const w = node.getBoundingClientRect?.().width;
      setSplitW(Number.isFinite(w) && w > 0 ? Math.round(w / scaleOf(w, node.offsetWidth)) : 0);
    };
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      splitRoRef.current = ro;
    }
    measure();   // 🔴 관찰을 걸고 나서 바로 한 번 잰다 — RO 의 첫 전달을 기다리면 한 프레임이 제한 없이 나간다.
  }, []);

  // px·상자 폭은 CSS px — 상자 rect(화면 px)는 상자의 배율로 나눈다(jsdom·1.0 은 1).
  const clampNotesW = useCallback((px) => {
    const box = splitRef.current?.getBoundingClientRect();
    const max = box ? notesMaxFor(box.width / scaleOf(box.width, splitRef.current.offsetWidth)) : 720;
    return Math.round(Math.min(Math.max(px, NOTES_MIN), max));
  }, []);

  const startSplitDrag = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    setSplitHandleHot(true);
    // 메모 폭 = (상자 오른끝 − clientX) ÷ 배율 — 둘 다 화면 px 라 뺀 뒤 CSS px 로 바꾼다.
    const onMove = (ev) => {
      const box = splitRef.current?.getBoundingClientRect();
      if (box) setNotesW(clampNotesW((box.right - ev.clientX) / scaleOf(box.width, splitRef.current.offsetWidth)));
    };
    const onUp = (ev) => {
      draggingRef.current = false;
      // 드래그를 마쳤을 때 커서가 아직 구분선 위면 파랑을 유지한다(새 mouseenter 가 안 오므로 직접 판정).
      const r = sepRef.current?.getBoundingClientRect();
      setSplitHandleHot(!!r && ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [clampNotesW]);

  useEffect(() => {
    try { window.localStorage.setItem('gwMeetingNotesW', String(notesW)); } catch (e) { /* 저장 실패는 무시 */ }
  }, [notesW]);

  // 상자가 736 미만이면 두 열이 안 들어간다 → 모바일과 같은 한 열 배치로 쌓는다.
  // 🔴 분할 영역 배치와 그에 딸린 모아보기 동작만 이 값을 본다 — 입력 방식·화면 크기 분기(발화 도구·
  //    재생바·탭바 여백 등)는 계속 `mobile` 이다(좁은 데스크톱이 모바일처럼 굴면 안 된다).
  const stacked = mobile || (splitW > 0 && splitW < SPLIT_MIN);
  // 그리는 폭 — 저장값(사용자 선호)은 건드리지 않고, 지금 상자에 맞게 줄여서만 그린다.
  // 🔴 그래서 창을 좁혔다 넓히면 사용자가 정한 폭이 그대로 돌아온다(옛 resize 리스너는 저장값을 덮었다).
  const notesWShown = splitW > 0
    ? Math.round(Math.min(Math.max(notesW, NOTES_MIN), notesMaxFor(splitW)))
    : notesW;

  const [sumOpen, setSumOpen] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [summaryVersions, setSummaryVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');
  const [selectedSummaryVersionId, setSelectedSummaryVersionId] = useState(null);
  const [restoringSummary, setRestoringSummary] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  // 머리 «검색»(클로바 묶음 D2b) — sticky 머리 아래 전체 폭 줄에서 전사·요약·메모·참석자를 한 번에 찾는다.
  const [searchOpen, setSearchOpen] = useState(false);
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [searchCursor, setSearchCursor] = useState(0);
  const [searchJumped, setSearchJumped] = useState(false);
  const [jumpPulseIndex, setJumpPulseIndex] = useState(null);
  // 전사 밖(요약·메모·참석자) 결과의 깜빡임 — 발화는 인덱스, 나머지는 요소 id 로 센다.
  const [jumpPulseKey, setJumpPulseKey] = useState(null);
  // 탭·참석자 줄을 먼저 펴야 대상이 생기는 이동 — 렌더 뒤 effect 에서 스크롤·깜빡임을 한다.
  const [pendingJump, setPendingJump] = useState(null);
  const [selectedEditIndexes, setSelectedEditIndexes] = useState([]);
  const [deletedEditSegs, setDeletedEditSegs] = useState([]);
  // 편집판 «시각 고치기»(클로바 묶음 D2c) — 클로바엔 시작/종료 숫자칸이 없다 → 기본 숨김이고
  // 이 토글을 켤 때만 보인다(시각이 잘못된 행은 저장 막힘 이유가 보이게 토글과 무관하게 보인다).
  const [timeEditOpen, setTimeEditOpen] = useState(false);
  // 머리의 참석자 줄(제목 옆 ⌄) · 점유율 창 · 화자 숨기기(클로바 묶음 C3a)
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [participantInputOpen, setParticipantInputOpen] = useState(false);
  const [participantSaving, setParticipantSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // ⋮ 에서 여는 창 둘(클로바 묶음 C3b) — 단어 바꾸기·단어장
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceInit, setReplaceInit] = useState(REPLACE_INIT_ALL);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  // 발화 도구(클로바 묶음 D1) — 마우스가 올라간 행과 키보드 포커스가 있는 행을 따로 센다.
  // 🔴 하나로 합치면 발화를 눌러 재생한 뒤 커서를 치워도 도구가 떠 있다(포커스가 남는다).
  const [hoverSeg, setHoverSeg] = useState(null);
  const [focusSeg, setFocusSeg] = useState(null);
  // 발화 ⋮ 펼침 상자를 위로 뒤집은 행 — { i, up, maxH }(아래 공간이 모자랄 때만 채운다).
  const [segMenuBox, setSegMenuBox] = useState(null);
  // 오른쪽 패널 «요약 | 메모» 탭(클로바 묶음 E1) + 메모 추가 초안
  const [sideTab, setSideTab] = useState('summary');
  // 요약 카드 안 «AI 맞춤 요약 | 시간대별 요약» 탭(클로바 묶음 E2) + 시간대별 요약을 만드는 중인 노트 id.
  // 🔴 만드는 중 표시는 노트 id 단위다 — bool 이면 노트를 바꿔도 앞 노트 호출이 끝날 때까지 새 노트 버튼이 꺼진다.
  // 🔴 `chaptersBusy` 는 여기(위쪽)에 둔다 — 아래 `canReplaceFromSearch` 가 읽으므로 더 아래에 두면 TDZ 다.
  const [sumKind, setSumKind] = useState('ai');
  const [chaptersBusyId, setChaptersBusyId] = useState(null);
  const chaptersBusy = chaptersBusyId != null && chaptersBusyId === active?.id;
  const [memoDraft, setMemoDraft] = useState('');
  // 오른쪽 패널 «모아보기» 판(클로바 묶음 D2a) — sideTab 의 세 번째 값 'collect'.
  // 닫을 때 열기 전 탭(요약/메모)으로 돌아가려고 그 값을 따로 들고 있는다.
  const [sideTabBeforeCollect, setSideTabBeforeCollect] = useState('summary');
  const [collectTab, setCollectTab] = useState('bookmark');
  const [collectHeadH, setCollectHeadH] = useState(0);   // sticky 머리 높이(판을 열 때·참석자 줄을 펼 때 잰다)
  const collectPanelRef = useRef(null);
  // 해제한 항목 자리 — 그 행이 사라지면 포커스가 BODY 로 떨어지므로 다음 항목으로 옮길 자리를 기억한다.
  const collectFocusRef = useRef(null);
  const playbarRef = useRef(null);     // 하단 재생바 — 발화 ⋮ 의 아래 한계
  const stickyHeadRef = useRef(null);  // sticky 머리 — 발화 ⋮ 의 위 한계
  const [hideSpeakers, setHideSpeakers] = useState(() => {
    try {
      return window.localStorage.getItem(HIDE_SPEAKERS_KEY) === '1';
    } catch (e) { /* 저장소 접근 불가 시 보이기 */ }
    return false;
  });
  const searchPulseTimer = useRef(null);
  const clearJumpPulse = () => {
    if (searchPulseTimer.current && typeof window !== 'undefined') {
      window.clearTimeout(searchPulseTimer.current);
      searchPulseTimer.current = null;
    }
  };
  // 검색 줄 닫기 = 검색어·순번·깜빡임까지 정리한다 — 닫아 둔 검색어가 칠하기를 남기면 안 된다.
  // 🔴 노트 전환·편집 시작·변환 완료 해제도 이 한 곳을 쓴다(아이콘만 숨기면 줄이 남는다).
  const resetSearch = () => {
    setSearchOpen(false);
    setTranscriptSearch('');
    setSearchCursor(0);
    setSearchJumped(false);
    setJumpPulseIndex(null);
    setJumpPulseKey(null);
    setPendingJump(null);
    clearJumpPulse();
  };
  const transcript = Array.isArray(active?.transcript) ? active.transcript : [];
  const recordingNotes = Array.isArray(active?.recording_notes) ? active.recording_notes : [];
  const keywords = Array.isArray(active?.keywords) ? active.keywords : [];
  const showControls = !active?.summary || sumOpen;
  const hasBottomAudio = !!(active?.status === 'done' && active?.has_audio);
  const audioLoading = hasBottomAudio && !audioUrl;   // 재생바는 즉시 노출, 오디오 blob 로드 전엔 컨트롤만 대기
  const timeText = (sec) => fmtDur(Math.max(0, Math.floor(sec || 0))) || '0:00';
  const totalDuration = Math.max(0, Math.floor(Number(active?.duration_sec || audioRef.current?.duration || 0)));
  const rangeMax = Math.max(totalDuration, Math.ceil(playSec || 0), 1);
  const rangeValue = Math.min(Math.max(0, Number(playSec || 0)), rangeMax);
  // 폴더 표시 = 폴더 목록에서 찾은 전체 경로(«A > B»)가 먼저 — 서버 상세 응답엔 폴더 이름이 없다.
  const folderName = (active?.folder_id != null ? pathText(folders, active.folder_id) : '')
    || active?.folder_name || active?.folder?.name || '';
  const createdText = active?.created_at ? fmtDate(active.created_at) : '';
  const updatedText = active?.updated_at ? fmtDate(active.updated_at) : '';
  const metaDate = createdText ? `${createdText}${updatedText && updatedText !== createdText ? ` (${updatedText} 수정)` : ''}` : '';
  const participants = Array.isArray(active?.participants) ? active.participants : [];
  // 폴더 경로는 길 수 있어 따로 말줄임 칸으로 그린다(아래 metaParts 앞) — 나머지는 « · » 로 잇는다.
  const metaParts = [
    metaDate,
    fmtDur(active?.duration_sec),
    participants.length > 0 ? `참석자 ${participants.length}명` : '',
  ].filter(Boolean);
  const summaryModes = [
    { value: 'default', label: '기본' },
    { value: 'folder', label: '폴더' },
    { value: 'custom', label: '직접' },
  ];
  const summaryModeLabel = (value) => {
    const chip = SUMMARY_VERSION_MODE_LABELS[value];
    if (typeof chip === 'string') return chip;
    return summaryModes.find((mode) => mode.value === value)?.label || value || '기본';
  };
  const selectedSummaryVersion = summaryVersions.find((v) => v.id === selectedSummaryVersionId) || summaryVersions[0] || null;
  const hasSummaryVersions = summaryVersions.length > 0;
  const folderPromptNotice = folderSummaryPromptNotice(active, folders);

  const loadSummaryVersions = useCallback(async ({ silent = false } = {}) => {
    if (!active?.id) {
      setSummaryVersions([]);
      setSelectedSummaryVersionId(null);
      setVersionsOpen(false);
      return;
    }
    if (!silent) {
      setVersionsLoading(true);
      setVersionsError('');
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/meetings/${active.id}/summaries`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data.detail || '버전을 불러오지 못했습니다.');
      const list = Array.isArray(data) ? data : [];
      setSummaryVersions(list);
      setSelectedSummaryVersionId((prev) => (list.some((v) => v.id === prev) ? prev : (list[0]?.id || null)));
      if (list.length === 0) setVersionsOpen(false);
    } catch (e) {
      if (!silent) setVersionsError(e.message || '버전을 불러오지 못했습니다.');
    } finally {
      if (!silent) setVersionsLoading(false);
    }
  }, [active?.id]);

  useEffect(() => {
    loadSummaryVersions({ silent: true });
  }, [active?.id, active?.summary, loadSummaryVersions]);

  useEffect(() => {
    if (!editing) {
      setSelectedEditIndexes([]);
      setDeletedEditSegs([]);
      // 🔴 «시각 고치기» 도 함께 끈다 — 안 끄면 다음 편집에 켜진 채로 들어간다.
      setTimeEditOpen(false);
    }
  }, [active?.id, editing]);

  // 다른 노트를 열면 참석자 줄·입력칸·점유율 창은 접힌 상태로 돌아가고, 발화 도구 표시도 초기화한다.
  // 🔴 메모 초안(memoDraft)도 비운다 — 안 비우면 A 노트에 쓰던 글이 B 노트에 저장된다.
  // 🔴 검색 줄도 닫고 검색어를 비운다 — 안 비우면 A 노트에서 찾던 말로 B 노트가 칠해진다.
  useEffect(() => {
    resetSearch();
    setPeopleOpen(false);
    setParticipantInputOpen(false);
    setShareOpen(false);
    setReplaceOpen(false);
    setReplaceInit(REPLACE_INIT_ALL);
    setGlossaryOpen(false);
    setHoverSeg(null);
    setFocusSeg(null);
    setSegMenuBox(null);
    setSideTab('summary');
    setSideTabBeforeCollect('summary');
    setCollectTab('bookmark');
    setMemoDraft('');
    // 요약 종류는 «AI 맞춤 요약» 으로 돌아간다(만드는 중 표시 chaptersBusyId 는 노트 id 로 갈리므로 두고 간다).
    setSumKind('ai');
  }, [active?.id]);

  useEffect(() => {
    try {
      if (hideSpeakers) window.localStorage.setItem(HIDE_SPEAKERS_KEY, '1');
      else window.localStorage.removeItem(HIDE_SPEAKERS_KEY);
    } catch (e) { /* 저장 실패는 무시 */ }
  }, [hideSpeakers]);

  // 🔴 점유율 창은 ⋮ 메뉴 항목으로 열려서 포커스가 창 밖이다 → Esc 는 document 에서 받는다.
  useEffect(() => {
    if (!shareOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShareOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shareOpen]);

  // ── 모아보기 여닫기(클로바 묶음 D2a) ──────────────────────────────────────
  // 🔴 열 때의 탭을 기억해 둔다 — 닫으면 «요약»/«메모» 중 보던 쪽으로 돌아간다.
  //    이미 열려 있을 때 또 열어도 기억값을 'collect' 로 덮지 않는다(연타해도 오염되지 않는다).
  const openCollect = () => {
    if (sideTab !== 'collect') setSideTabBeforeCollect(sideTab);
    setSideTab('collect');
  };
  const closeCollect = () => setSideTab(sideTabBeforeCollect || 'summary');

  // 편집을 시작하면 모아보기·검색 줄을 닫는다 — 머리 아이콘만 숨기면 판·줄이 남는다
  // (해제도 편집 중엔 꺼져 있다).
  useEffect(() => {
    if (!editing) return;
    setSideTab((cur) => (cur === 'collect' ? (sideTabBeforeCollect || 'summary') : cur));
    resetSearch();
  }, [editing]);

  // 변환이 완료 상태를 벗어나면(화자분리 다시 실행 등) 검색 줄도 닫는다 — 요약·메모·전사는
  // 🔴 `status === 'done'` 블록 안에서만 그려지므로, 줄만 남으면 이동할 대상 DOM 이 없는데도 n/N 을 센다.
  useEffect(() => {
    if (active?.status !== 'done') resetSearch();
  }, [active?.status]);

  // 데스크톱 sticky 판의 top·판 안 maxHeight 기준 = sticky 머리 높이.
  // 🔴 판을 연 채 «참석자» 줄을 펴면 머리가 커지므로 peopleOpen 이 바뀔 때도 다시 잰다.
  // 🔴 검색만 열어도 재야 한다 — 모아보기를 연 적 없으면 값이 0 이라 요약·메모의 scrollMarginTop 이
  //    8px 이 되어 대상이 sticky 머리 밑으로 들어간다.
  // 웹 배율(<html> zoom)이 걸리면 rect 높이는 화면 px, top·maxHeight style 은 CSS px 다 → 머리의 배율로 나눈다
  // (jsdom·1.0 은 1 · 모바일은 그대로 — 모바일 경로는 바꾸지 않는다).
  useEffect(() => {
    if (sideTab !== 'collect' && !searchOpen) return;
    const el = stickyHeadRef.current;
    const r = el?.getBoundingClientRect?.();
    const h = r?.height;
    if (typeof h === 'number') setCollectHeadH(Math.round(mobile ? h : h / scaleOf(r.width, el.offsetWidth)));
  }, [sideTab, peopleOpen, searchOpen]);

  // 한 열(모바일·좁은 상자)은 판이 전사 위(세로 쌓임)라, 전사를 내려 본 뒤 열면 판이 화면 밖이다 → 끌어온다.
  // 🔴 `?.` 두 번 — jsdom 에는 scrollIntoView 가 없어 가드 없이 부르면 트리가 통째로 언마운트된다.
  // 🔴 머리 높이는 여기서 직접 잰다 — 위 effect 의 setCollectHeadH 는 다음 렌더에야 판 style 에 들어가,
  //    처음 열 때(0)·닫은 뒤 참석자 줄을 펴고 다시 열 때(이전 값) 판 머리가 sticky 머리 밑으로 들어갔다.
  //    collectHeadH 는 deps 에 넣지 않는다 — 판을 연 채 참석자 줄을 펴면 판을 다시 끌어와 화면이 튄다.
  useEffect(() => {
    if (sideTab !== 'collect' || !stacked) return;
    const panel = collectPanelRef.current;
    if (!panel) return;
    const el = stickyHeadRef.current;
    const r = el?.getBoundingClientRect?.();
    const h = r?.height;
    if (typeof h === 'number') {
      const hh = Math.round(mobile ? h : h / scaleOf(r.width, el.offsetWidth));   // 화면 px → CSS px(위 effect 와 같은 식)
      panel.style.scrollMarginTop = `${hh + COLLECT_GAP}px`;
      setCollectHeadH(hh);   // 다음 렌더의 판 style 값도 같은 값이 된다(React 가 덮어써도 어긋나지 않는다)
    }
    panel.scrollIntoView?.({ block: 'start' });
  }, [sideTab, stacked]);

  // 해제 뒤 포커스 옮기기 — 🔴 markSaving 이 켜진 렌더에서는 남은 해제 버튼이 전부 disabled 라
  //    focus() 가 안 먹는다. 저장이 끝난(markSaving=false) 렌더에서 옮긴다.
  useEffect(() => {
    const want = collectFocusRef.current;
    if (!want || markSaving) return;
    collectFocusRef.current = null;
    if (sideTab !== 'collect' || want.tab !== collectTab || typeof document === 'undefined') return;
    const buttons = Array.from(document.querySelectorAll('[data-collect-unmark="1"]'));
    const next = buttons[want.pos] || buttons[buttons.length - 1] || document.getElementById(`collect-tab-${collectTab}`);
    next?.focus?.();
  }, [transcript, markSaving]);

  const openSummaryVersions = async () => {
    setVersionsOpen(true);
    await loadSummaryVersions();
  };

  const restoreSummaryVersion = async () => {
    if (!active?.id || !selectedSummaryVersion || restoringSummary) return;
    if (!(await confirmDialog('현재 요약을 이 버전으로 교체합니다'))) return;
    setRestoringSummary(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/meetings/${active.id}/summaries/${selectedSummaryVersion.id}/restore`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || '복원에 실패했습니다.');
      setActive((a) => (a && a.id === active.id
        // 복원한 옛 버전에는 그 반증 ⚠ 판정이 없다 — 이전 줄을 지운다.
        ? { ...a, summary: data.summary, summary_prompt_used: data.summary_prompt_used, has_summary: true, keywords: data.keywords ?? a.keywords, summary_falsify_warn: '' }
        : a));
      setVersionsOpen(false);
      await loadSummaryVersions({ silent: true });
    } catch (e) {
      alertDialog('복원 실패: ' + (e.message || '요약 버전을 복원하지 못했습니다.'));
    } finally {
      setRestoringSummary(false);
    }
  };

  const handleGenSummary = async () => {
    if (sumMode === 'custom' && !(customPrompt || '').trim()) {
      setSumOpen(true);
      genSummary();
      return;
    }
    await genSummary();
    await loadSummaryVersions({ silent: true });
    setSumOpen(false);
  };

  // 참석자 저장 — PATCH /api/meetings/{id} {participants}
  // 🔴 JSON 본문이라 `Content-Type: application/json` 을 반드시 붙인다. 빼면 브라우저가 text/plain 으로
  //    보내고 `update_meeting(body: MeetingPatch)` 가 422 를 낸다(요약 버전 호출은 본문이 없어 헤더도 없다).
  // 🔴 낙관적 갱신을 하지 않는다 — 서버가 정리한(공백·중복 제거) 응답 participants 로만 화면을 바꾼다.
  const saveParticipants = async (next) => {
    if (!active?.id || participantSaving) return;
    const noteId = active.id;
    setParticipantSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/meetings/${noteId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ participants: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 🔴 422 의 detail 은 배열(FastAPI 검증 오류)이라 문자열일 때만 그대로 보인다.
        const detail = data && data.detail;
        alertDialog('참석자 저장 실패: ' + (typeof detail === 'string' && detail.trim() ? detail : '저장하지 못했습니다.'));
        return;
      }
      const saved = Array.isArray(data.participants) ? data.participants : [];
      setActive((a) => (a && a.id === noteId ? { ...a, participants: saved } : a));
    } catch (e) {
      alertDialog('참석자 저장 실패: 저장하지 못했습니다.');
    } finally {
      setParticipantSaving(false);
    }
  };

  const addParticipant = (raw) => {
    const name = String(raw ?? '').trim();
    // 🔴 fetch 보다 먼저 동기로 닫는다 — 열어 두면 남은 음절이 한 명 더로 저장된다.
    setParticipantInputOpen(false);
    if (!name || participants.length >= PARTICIPANTS_MAX) return;
    // 서버 `_participants_clean` 과 같은 기준(대소문자 무시)으로 이미 있는 이름이면 요청하지 않는다.
    const key = name.toLocaleLowerCase();
    if (participants.some((p) => String(p).trim().toLocaleLowerCase() === key)) return;
    saveParticipants([...participants, name]);
  };

  const removeParticipant = (name) => {
    saveParticipants(participants.filter((p) => p !== name));
  };

  const participantInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setParticipantInputOpen(false);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // 한글 입력 중의 Enter(조합 확정)는 추가가 아니다 — 조합이 끝난 뒤 keydown 이 한 번 더 온다.
    if (e.nativeEvent?.isComposing) return;
    // 🔴 state 가 아니라 input 의 현재 값을 읽는다(조합 중에는 state 가 늦다 — NewNoteView 와 같은 규칙).
    addParticipant(e.currentTarget.value);
  };

  // 말한 시간 표기 — fmtDur 는 1시간 이상을 '72:30' 으로 보이므로 점유율 창은 h:mm:ss 를 쓴다.
  const shareTimeText = (sec) => {
    const total = Math.max(0, Math.round(Number(sec) || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`;
  };

  const markCounts = transcript.reduce((acc, s) => {
    if (s.bookmark) acc.bookmark += 1;
    if (s.highlight) acc.highlight += 1;
    if ((s.memo || '').trim()) acc.memo += 1;
    return acc;
  }, { bookmark: 0, highlight: 0, memo: 0 });
  // 메모 탭의 «발화 메모» 모아보기 — markCounts.memo 와 같은 기준(원래 인덱스를 들고 간다).
  const segMemos = transcript.map((s, i) => ({ s, i })).filter(({ s }) => (s.memo || '').trim());
  const memoTotal = recordingNotes.length + segMemos.length;
  // 모아보기 판의 목록 — segMemos 와 같은 꼴(원래 인덱스를 들고 간다).
  const collectItems = transcript.map((s, i) => ({ s, i })).filter(({ s }) => !!s[collectTab]);
  // 🔴 전사 위 표시 필터 탭은 없앴다(클로바 묶음 D2a) — 북마크·하이라이트는 모아보기 판,
  //    메모는 «메모» 탭에서 본다. 전사에는 모든 발화가 그대로 보인다.
  const visibleTranscript = transcript.map((s, i) => ({ s, i }));
  const searchNeedle = transcriptSearch.trim();
  const hasSearch = searchNeedle.length > 0;
  const searchNeedleLower = searchNeedle.toLocaleLowerCase();
  const searchHit = (text) => String(text ?? '').toLocaleLowerCase().includes(searchNeedleLower);
  // 검색 결과 = 요소 하나가 한 건(요약은 안에 몇 번 나와도 1건).
  // 🔴 순서는 참석자 → 전사 발화 → 요약 → 녹음 메모 → 발화 메모 로 고정한다 — 모바일은 요약·메모가
  //    전사 위로 쌓이지만, 같은 검색어의 순번이 화면 폭에 따라 달라지면 안 된다.
  const searchMatches = [];
  if (hasSearch) {
    participants.forEach((name, k) => { if (searchHit(name)) searchMatches.push({ kind: 'people', key: `mpeople-${k}`, i: k }); });
    visibleTranscript.forEach(({ s, i }) => { if (searchHit(s.text)) searchMatches.push({ kind: 'seg', key: `mseg-${i}`, i }); });
    if (searchHit(active?.summary)) searchMatches.push({ kind: 'summary', key: 'msummary', i: 0 });
    recordingNotes.forEach((n, k) => { if (searchHit(n.text)) searchMatches.push({ kind: 'note', key: `mnote-${k}`, i: k }); });
    segMemos.forEach(({ s, i }) => { if (searchHit(s.memo)) searchMatches.push({ kind: 'segmemo', key: `msegmemo-${i}`, i }); });
  }
  const searchTotal = searchMatches.length;
  const searchKinds = searchMatches.reduce((acc, m) => ({ ...acc, [m.kind]: true }), {});
  const safeSearchCursor = searchTotal > 0 ? Math.min(searchCursor, searchTotal - 1) : 0;
  // 요약·메모 결과로 이동할 때 대상이 sticky 머리 밑으로 들어가지 않게 두는 여백(모아보기 판과 같은 값).
  const jumpScrollMarginTop = `${collectHeadH + COLLECT_GAP}px`;
  // 전사 밖 결과의 깜빡임 — 발화 깜빡임과 같은 바깥선이다.
  // 🔴 모서리는 호출부가 정한다(참석자 칩은 알약 모양이라 8px 로 덮으면 모양이 깨진다).
  const jumpPulseStyle = (key, radius = '8px') => ({
    borderRadius: radius,
    boxShadow: jumpPulseKey === key ? '0 0 0 2px #2563eb inset' : 'none',
    transition: 'box-shadow .15s',
  });
  const transcriptGroups = visibleTranscript.reduce((groups, item) => {
    const speakerKey = item.s.speaker || '__no_speaker__';
    const last = groups[groups.length - 1];
    if (last && last.speakerKey === speakerKey && last.items[last.items.length - 1].i + 1 === item.i) {
      last.items.push(item);
      return groups;
    }
    groups.push({ speakerKey, items: [item] });
    return groups;
  }, []);
  const selectedEditSet = new Set(selectedEditIndexes.filter((i) => i >= 0 && i < editSegs.length));
  const selectedEditCount = selectedEditSet.size;
  const allEditSelected = editSegs.length > 0 && selectedEditCount === editSegs.length;
  // 편집판도 읽기와 같은 화자 묶음 배치다(클로바 묶음 D2c) — 묶음은 **원래 화자 키**로 묶는다.
  // 🔴 바꾼 이름으로 묶지 않는다 — 두 화자를 같은 이름으로 바꾸면 같은 이름 머리가 연달아 둘
  //    생길 수 있지만(저장하면 합쳐진다), 묶음이 중간에 뒤섞이는 쪽이 더 헷갈린다.
  const editTranscriptGroups = editSegs.reduce((groups, s, i) => {
    const speakerKey = s.speaker || '__no_speaker__';
    const last = groups[groups.length - 1];
    if (last && last.speakerKey === speakerKey) {
      last.items.push({ s, i });
      return groups;
    }
    groups.push({ speakerKey, items: [{ s, i }] });
    return groups;
  }, []);
  // 묶음 머리 이름 — 사용자가 바꾼 이름(spkMap)이 있으면 그 이름, 아니면 읽기 화면과 같은 표시 이름.
  // 🔴 `spkMap[sp] ?? dispSpk(...)` 로 쓰면 startEdit 이 원래 키를 그대로 채우므로(SPEAKER_00)
  //    읽기 화면의 «참석자 1» 대신 날코드가 보인다. 이름을 비우면 다시 표시 이름으로 돌아간다.
  const editSpeakerName = (sp) => {
    const renamed = spkMap[sp];
    return renamed && renamed !== sp ? renamed : dispSpk(sp, spkDisp);
  };

  const parseEditSecond = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const editSecondText = (value) => {
    const n = parseEditSecond(value);
    return n === null ? '-' : timeText(n);
  };
  const editTimingInvalid = (s) => {
    const st = parseEditSecond(s.start);
    const en = parseEditSecond(s.end);
    return st === null || en === null || st < 0 || en < 0 || st > en;
  };
  const updateEditSeg = (index, patch) => {
    setEditSegs((arr) => arr.map((x, j) => (j === index ? { ...x, ...patch } : x)));
  };
  const markEditSegDeleted = (index) => {
    const seg = editSegs[index];
    if (!seg) return;
    setDeletedEditSegs((arr) => [...arr, { seg: { ...seg }, index }]);
    setEditSegs((arr) => arr.filter((_, j) => j !== index));
    setSelectedEditIndexes((arr) => arr.filter((n) => n !== index).map((n) => (n > index ? n - 1 : n)));
  };
  const restoreEditSeg = (deletedIndex) => {
    const deleted = deletedEditSegs[deletedIndex];
    if (!deleted) return;
    setEditSegs((arr) => {
      const next = [...arr];
      const at = Math.max(0, Math.min(deleted.index, next.length));
      next.splice(at, 0, { ...deleted.seg });
      return next;
    });
    setDeletedEditSegs((arr) => arr.filter((_, j) => j !== deletedIndex));
    setSelectedEditIndexes([]);
  };
  const toggleEditSelection = (index) => {
    setSelectedEditIndexes((arr) => (arr.includes(index) ? arr.filter((n) => n !== index) : [...arr, index]));
  };
  const toggleAllEditSelection = () => {
    setSelectedEditIndexes(allEditSelected ? [] : editSegs.map((_, i) => i));
  };
  const bulkDeleteEditSelection = () => {
    if (selectedEditCount <= 0) return;
    const selected = Array.from(selectedEditSet).sort((a, b) => a - b);
    const selectedLookup = new Set(selected);
    const deleted = selected.map((index) => ({ seg: { ...editSegs[index] }, index })).filter((item) => item.seg);
    setDeletedEditSegs((arr) => [...arr, ...deleted]);
    setEditSegs((arr) => arr.filter((_, i) => !selectedLookup.has(i)));
    setSelectedEditIndexes([]);
  };
  const validateEditBeforeSave = () => {
    if (editSegs.length <= 0) {
      alertDialog('최소 1개 세그먼트가 필요합니다.');
      return false;
    }
    for (let i = 0; i < editSegs.length; i += 1) {
      if (editTimingInvalid(editSegs[i])) {
        alertDialog(`${i + 1}번 세그먼트 시간을 확인하십시오. 시작은 0 이상이고 종료보다 클 수 없습니다.`);
        return false;
      }
    }
    return true;
  };
  const handleSaveEdit = () => {
    if (!validateEditBeforeSave()) return;
    saveEdit();
  };
  const handleRagOpt = async () => {
    if (!active || !setRagOpt) return;
    const enabled = !active.rag_opt_in;
    const message = enabled
      ? "이 회의록을 문서 기반 답변(RAG) 검색에 포함할까요?\n폴더를 공유한 사용자의 검색에도 노출됩니다."
      : "RAG 등록을 해제할까요? 검색에서 즉시 제외됩니다.";
    if (!(await confirmDialog(message))) return;
    setRagOpt(active, enabled);
  };

  const segActionBtn = (on, color) => ({
    width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    padding: '7px 9px',
    borderRadius: '7px',
    border: `1px solid ${on ? T.color.outlineBtnBorder : 'transparent'}`,
    background: on ? T.color.selectedBg : '#fff',
    color: on ? color : '#64635f',
    fontWeight: on ? 700 : 500,
    cursor: markSaving ? 'not-allowed' : 'pointer',
    opacity: markSaving ? 0.55 : 1,
  });
  const statusBox = (color, bg, border) => ({
    color,
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: T.radius.md,
    padding: '10px 12px',
    fontSize: T.font.md,
    lineHeight: 1.55,
    marginBottom: '16px',
    ...softWrap,
  });
  // 변환 대기·변환 중·화자 분석 중 안내 끝 한 구절(C3c) — 중지 함수를 받았을 때만. 모양은 failed 줄의 흐린 안내와 같다.
  const cancelHint = cancelStt ? <> <span style={{ color: '#64635f' }}>⋮ 메뉴에서 중지할 수 있습니다.</span></> : null;
  const actionItems = (() => {
    const text = active?.summary || '';
    const m = text.match(/(?:^|\n)##\s*(?:액션 아이템|할 일|Action Items?)\s*\n([\s\S]*?)(?=\n##\s|$)/i);
    if (!m) return [];
    return m[1].split('\n')
      .map((line) => line.replace(/^[-*]\s*/, '').trim())
      .filter((line) => line && !/^없음[.]?$/.test(line))
      .slice(0, 12);
  })();

  const buildTranscriptText = () => {
    const raw = (active?.transcript_text || '').trim();
    if (raw) return raw;
    return transcript.map((s) => {
      const speaker = s.speaker ? `${dispSpk(s.speaker, spkDisp)} ` : '';
      return `[${timeText(s.start)}] ${speaker}${s.text || ''}`.trim();
    }).join('\n');
  };

  // 전사 전체 복사와 발화 하나 복사가 같이 쓰는 경로.
  // 🔴 폴백의 execCommand 가 false 를 돌려주면 복사가 안 된 것이다 — 성공으로 넘기지 않고 던진다.
  const copyPlainText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    if (!ok) throw new Error('execCommand(copy) 가 false 를 돌려줬습니다.');
  };

  const copyTranscript = async () => {
    const text = buildTranscriptText();
    if (!text) return;
    try {
      await copyPlainText(text);
      setCopyOk(true);
      window.setTimeout(() => setCopyOk(false), 1400);
    } catch {
      alertDialog('전사 복사에 실패했습니다.');
    }
  };

  // 편집 중 «복사» — 아직 저장하지 않은 내용을 그대로 복사한다(클로바 묶음 D2c).
  // 🔴 형식은 서버 `_plain_text_segs` 와 글자까지 같게 둔다 — 줄마다 `[이름] 글`, 글이 빈 줄은 뺀다.
  //    그래야 «완료» 뒤 읽기 화면 복사(transcript_text 그대로)와 결과가 같다.
  // 🔴 «원래 화자 키가 있나» 가 아니라 «이름이 비었나» 로 가른다 — 사용자가 이름을 지우면 저장
  //    경로가 빈 이름을 null 로 보내고 서버는 접두 없이 글만 낸다(`[] 글` 이 아니다).
  const buildEditTranscriptText = () => editSegs.map((s) => {
    const t = String(s.text ?? '').trim();
    if (!t) return null;
    const name = s.speaker ? (spkMap[s.speaker] ?? s.speaker) : null;
    return name ? `[${name}] ${t}` : t;
  }).filter((line) => line !== null).join('\n');

  const copyEditTranscript = async () => {
    const text = buildEditTranscriptText();
    if (!text) return;
    try {
      await copyPlainText(text);
      setCopyOk(true);
      window.setTimeout(() => setCopyOk(false), 1400);
    } catch {
      alertDialog('전사 복사에 실패했습니다.');
    }
  };

  // 발화 ⋮ «텍스트 복사» — 그 발화의 글만 클립보드로.
  const copySegmentText = async (s) => {
    const text = String(s?.text ?? '');
    if (!text) return;
    try {
      await copyPlainText(text);
      toast('복사했습니다.');
    } catch {
      alertDialog('복사하지 못했습니다.');
    }
  };

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };
  const skipAudio = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    const max = Number.isFinite(a.duration) ? a.duration : totalDuration;
    const next = Math.max(0, Math.min((a.currentTime || 0) + delta, max || Math.max(0, (a.currentTime || 0) + delta)));
    a.currentTime = next;
    setPlaySec(next);
  };
  const changeRate = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  };
  // 전사 밖 결과(요약·메모·참석자)의 1초 깜빡임 — 발화 깜빡임과 같은 타이머를 쓴다.
  // 🔴 한 번에 하나만 깜빡인다 → 발화 쪽 표시를 함께 끈다.
  const pulseJumpKey = (key) => {
    clearJumpPulse();
    setJumpPulseIndex(null);
    setJumpPulseKey(key);
    if (typeof window !== 'undefined') {
      searchPulseTimer.current = window.setTimeout(() => {
        setJumpPulseKey((current) => (current === key ? null : current));
        searchPulseTimer.current = null;
      }, 1000);
    }
  };
  // 전사의 그 발화로 스크롤 + 1초 깜빡임 — 검색 이동과 모아보기 항목 클릭이 같이 쓴다.
  // 🔴 검색 상태(n/N·searchJumped)는 건드리지 않는다 — 모아보기에서 눌러도 검색 순번이 바뀌면 안 된다.
  const scrollToSeg = (targetIndex) => {
    if (typeof document !== 'undefined') {
      document.getElementById(`mseg-${targetIndex}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
    clearJumpPulse();
    setJumpPulseKey(null);
    setJumpPulseIndex(targetIndex);
    if (typeof window !== 'undefined') {
      searchPulseTimer.current = window.setTimeout(() => {
        setJumpPulseIndex((current) => (current === targetIndex ? null : current));
        searchPulseTimer.current = null;
      }, 1000);
    }
  };
  // 검색 결과 하나로 이동 — 종류마다 보이는 자리가 달라 먼저 그 자리를 펴고(탭·참석자 줄),
  // 스크롤·깜빡임은 렌더가 끝난 뒤 아래 effect 에서 한다.
  const jumpToSearchMatch = (cursor) => {
    if (searchTotal <= 0) return;
    const nextCursor = ((cursor % searchTotal) + searchTotal) % searchTotal;
    const hit = searchMatches[nextCursor];
    setSearchCursor(nextCursor);
    setSearchJumped(true);
    if (hit.kind === 'seg') {
      scrollToSeg(hit.i);
      return;
    }
    if (hit.kind === 'summary') {
      setSideTab('summary');
      setSumKind('ai');   // 요약 본문 #msummary 는 «AI 맞춤 요약» 탭에만 있다
    } else if (hit.kind === 'note' || hit.kind === 'segmemo') setSideTab('memo');
    else if (hit.kind === 'people') setPeopleOpen(true);
    // 참석자는 머리 안이라 스크롤하지 않는다(깜빡임만).
    setPendingJump({ key: hit.key, scroll: hit.kind !== 'people' });
  };
  const moveSearch = (delta) => {
    if (searchTotal <= 0) return;
    const nextCursor = searchJumped ? safeSearchCursor + delta : (delta > 0 ? safeSearchCursor : safeSearchCursor - 1);
    jumpToSearchMatch(nextCursor);
  };
  const handleSearchChange = (e) => {
    setTranscriptSearch(e.target.value);
    setSearchCursor(0);
    setSearchJumped(false);
    setJumpPulseIndex(null);
    setJumpPulseKey(null);
    clearJumpPulse();
  };
  const clearSearchText = () => {
    setTranscriptSearch('');
    setSearchCursor(0);
    setSearchJumped(false);
    setJumpPulseIndex(null);
    setJumpPulseKey(null);
    clearJumpPulse();
  };
  const toggleSearch = () => {
    if (searchOpen) resetSearch();
    else setSearchOpen(true);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      resetSearch();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    moveSearch(e.shiftKey ? -1 : 1);
  };
  // 검색 줄 [단어 바꾸기] — 찾을 단어에 검색어를 채우고 대상은 «검색이 맞은 종류» 만 켠 채 연다.
  // 🔸 검색이 전사·요약·메모를 다 보여 주므로 «검색에 맞은 곳 = 바뀌는 곳» 으로 맞춘다(창에서 더 켤 수 있다).
  // 🔸 한 건도 못 맞췄으면 고를 근거가 없으니 ⋮ 기본과 같이 셋 다 켠다.
  // 🔸 100자를 넘는 검색어는 잘라 넘기면 검색한 범위보다 넓게 바뀌므로 버튼 자체를 감춘다.
  const canReplaceFromSearch = hasSearch
    && searchNeedle.length <= REPLACE_WORD_MAX
    && active?.status === 'done'
    && !summarizing && !chaptersBusy
    && !editing;
  const openReplaceFromSearch = () => {
    const matched = {
      transcript: !!searchKinds.seg,
      summary: !!searchKinds.summary,
      notes: !!(searchKinds.note || searchKinds.segmemo),
    };
    const none = !matched.transcript && !matched.summary && !matched.notes;
    setReplaceInit({
      find: searchNeedle.slice(0, REPLACE_WORD_MAX),
      targets: none ? { transcript: true, summary: true, notes: true } : matched,
    });
    setReplaceOpen(true);
  };

  // 🔴 `?.` 두 번 — jsdom 에는 scrollIntoView 가 없어 가드 없이 부르면 트리가 통째로 언마운트된다.
  // 🔴 `block: 'start'` 다 — center 면 키 큰 요약의 위끝이 sticky 머리 밑으로 들어간다(scrollMarginTop 이 먹지 않는다).
  useEffect(() => {
    if (!pendingJump) return;
    if (pendingJump.scroll && typeof document !== 'undefined') {
      document.getElementById(pendingJump.key)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
    pulseJumpKey(pendingJump.key);
    setPendingJump(null);
  }, [pendingJump, sideTab, peopleOpen]);

  useEffect(() => () => clearJumpPulse(), []);

  const iconButtonStyle = (tone = '#64635f') => ({
    width: '34px',
    height: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: '1px solid #e5e5e2',
    background: '#fff',
    color: tone,
    cursor: 'pointer',
    padding: 0,
    boxSizing: 'border-box',
  });
  const menuItemStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    border: 'none',
    background: '#fff',
    color: '#32312e',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  };
  // <details> 는 항목을 눌러도 저절로 닫히지 않는다 — 누른 항목의 메뉴를 직접 닫아 준다.
  const closeDetailMenu = (e) => {
    e.currentTarget.closest('details')?.removeAttribute('open');
  };
  // 도구줄의 두 메뉴(다운로드·⋮)가 동시에 열려 겹치지 않게 — 방금 열린 쪽만 남긴다.
  // 🔴 닫힐 때(!open)는 아무것도 하지 않는다. 가드를 빼면 ⋮ 를 열며 닫은 다운로드가 쏜
  //    toggle 이 다시 ⋮ 을 닫아 둘 다 닫힌다.
  const closeOtherMenus = (e) => {
    const me = e.currentTarget;
    if (!me.open) return;
    me.parentElement.querySelectorAll('details[open]').forEach((d) => { if (d !== me) d.removeAttribute('open'); });
  };
  // 발화 ⋮ 는 한 번에 하나만 — 🔴 행마다 부모가 달라 `closeOtherMenus`(같은 부모 안 형제)로는 안 닫힌다.
  // SegmentMenu 가 함수 호출로 바뀌어 저절로 다시 마운트되지 않으므로 여기서 직접 닫아 준다.
  const closeOtherSegMenus = (e) => {
    const me = e.currentTarget;
    if (!me.open || typeof document === 'undefined') return;
    document.querySelectorAll('details[data-seg-menu="1"][open]').forEach((d) => { if (d !== me) d.removeAttribute('open'); });
  };
  // 발화 ⋮ 를 열 때 아래 공간을 재서 모자라면 위로 뒤집는다(모바일 맨 아래 발화가 재생바에 가렸다).
  // 아래 한계 = 하단 재생바 위끝(없으면 화면 아래 — 모바일은 하단 탭바 TABBAR_H 를 뺀다),
  // 위 한계 = sticky 머리 아래끝(머리·제목을 덮지 않게).
  // 🔴 zIndex 를 올려 해결하지 않는다 — 조상 쌓임 맥락에 갇히고, 먹어도 항목이 재생바를 덮는다.
  const onSegMenuToggle = (e, i) => {
    closeOtherSegMenus(e);
    const details = e.currentTarget;
    if (!details.open) {
      // 🔴 무조건 비우지 않는다 — 다른 ⋮ 를 새로 열면 앞 메뉴의 닫힘 toggle 이 새 메뉴의
      //    열림 toggle 뒤에 와서 방금 정한 «위로» 를 지운다.
      setSegMenuBox((cur) => (cur && cur.i === i ? null : cur));
      return;
    }
    if (typeof window === 'undefined') return;
    const summary = details.querySelector('summary');
    const panel = details.querySelector('[data-seg-menu-panel="1"]');
    if (!summary || !panel) return;
    const rect = summary.getBoundingClientRect();
    const menuH = panel.offsetHeight || 0;
    // 웹 배율(<html> zoom)이 걸리면 rect·innerHeight 는 화면 px, menuH·maxH 는 CSS px 다 → 화면 px 를 배율로 나눈다
    // (jsdom·1.0 은 1 · 모바일은 1 로 고정 — 모바일 경로는 바꾸지 않는다).
    const s = mobile ? 1 : scaleOf(rect.width, summary.offsetWidth);
    const bar = playbarRef.current?.getBoundingClientRect();
    const limit = bar ? bar.top / s : window.innerHeight / s - (mobile ? TABBAR_H : 0);
    const headBottom = (stickyHeadRef.current?.getBoundingClientRect().bottom || 0) / s;
    const below = limit - rect.bottom / s - SEG_MENU_GAP;
    const above = rect.top / s - headBottom - SEG_MENU_GAP;
    const up = menuH > below && above > below;
    const space = up ? above : below;
    const maxH = menuH > space ? Math.max(SEG_MENU_MIN_H, Math.round(space - 8)) : null;
    setSegMenuBox({ i, up, maxH });
  };
  // ── 메모 탭(클로바 묶음 E1) ────────────────────────────────────────────────
  // 노트 단위 메모 칸은 DB 에 없다 → 이미 있는 «녹음 메모»(recording_notes)에 한 줄 더한다.
  const canEditNotes = typeof saveRecordingNotes === 'function';
  const notesFull = recordingNotes.length >= RECORDING_NOTES_MAX;
  const memoLocked = notesSaving || editing;
  // 오디오가 없으면 재생 위치가 없다 → 0:00 에 남긴다.
  const memoTime = hasBottomAudio ? Math.floor(Math.max(0, Number(playSec) || 0)) : 0;
  const addRecordingMemo = async (raw) => {
    const text = String(raw ?? '').trim();
    if (!canEditNotes || !text || memoLocked || notesFull) return;
    const t = memoTime;
    // 같은 초(화면에 같은 시각)에 이미 있는 메모 뒤로 간다.
    // 🔴 `n.time <= t` 로 비교하면 소수(75.37)가 새 75 뒤로 밀린다 — 내림한 값으로 센다.
    let at = 0;
    recordingNotes.forEach((n, k) => { if (Math.floor(Number(n.time) || 0) <= t) at = k + 1; });
    const next = [...recordingNotes];
    next.splice(at, 0, { time: t, text });
    const ok = await saveRecordingNotes(next);
    if (ok) setMemoDraft('');
  };
  const memoDraftKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    // 한글 조합 중의 Enter(조합 확정)는 추가가 아니다 — 조합이 끝난 뒤 keydown 이 한 번 더 온다.
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    // 🔴 state 가 아니라 입력칸의 현재 값을 읽는다(조합 직후 state 는 한 박자 늦다 — 참석자 입력과 같은 규칙).
    addRecordingMemo(e.currentTarget.value);
  };
  const removeRecordingMemo = async (i) => {
    if (!canEditNotes || memoLocked) return;
    if (!(await confirmDialog('이 메모를 삭제할까요?'))) return;
    saveRecordingNotes(recordingNotes.filter((_, k) => k !== i));
  };
  const cardStyle = {
    background: T.color.surface,
    borderRadius: T.radius.md,
    boxShadow: T.shadow.card,
    padding: mobile ? '14px' : '18px',
    border: `1px solid ${T.color.border}`,
  };

  const SpeakerAvatar = ({ speaker }) => {
    const av = speakerAvatar(speaker, spkDisp);
    return (
      <span style={{
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: av.color,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 800,
        flexShrink: 0,
        boxShadow: '0 1px 2px rgba(15,23,42,.14)',
      }}>
        {av.label}
      </span>
    );
  };

  // 표식 버튼 셋(북마크·하이라이트·메모) — 데스크톱 발화 도구 줄과 모바일 ⋮ 가 같은 testid·동작을 쓴다.
  // 🔴 한 화면에 같은 testid 를 둘 그리지 않는다(데스크톱 = 도구 줄, 모바일 = ⋮ 안).
  const segMarkButtons = (s, i, style) => ([
    <button key="bookmark" data-testid={`mseg-${i}-bookmark`} disabled={markSaving}
      onClick={(e) => { closeDetailMenu(e); toggleSegFlag(i, 'bookmark'); }} style={style(!!s.bookmark, '#2563eb')}>북마크</button>,
    <button key="highlight" data-testid={`mseg-${i}-highlight`} disabled={markSaving}
      onClick={(e) => { closeDetailMenu(e); toggleSegFlag(i, 'highlight'); }} style={style(!!s.highlight, '#b45309')}>하이라이트</button>,
    <button key="memo" data-testid={`mseg-${i}-memo`} disabled={markSaving}
      onClick={(e) => { closeDetailMenu(e); editSegMemo(i); }} style={style(!!(s.memo || '').trim(), '#047857')}>메모</button>,
  ]);

  // 도구 줄 안에서는 세로로 쌓이지 않게 폭을 글자에 맞춘다(segActionBtn 은 width 100% 다).
  const segToolBtn = (on, color) => ({ ...segActionBtn(on, color), width: 'auto', whiteSpace: 'nowrap' });

  // 발화 도구 줄(클로바 묶음 D1) — 🔴 행 위에 겹쳐 띄워 본문 폭을 차지하지 않는다.
  // 🔴 평소엔 opacity 0 이고 pointer-events·display·visibility 로는 숨기지 않는다 —
  //    Tab 으로 닿아야 하고, e2e(Playwright)가 투명도 0 도 «보임» 으로 보고 그대로 누른다.
  const SegmentTools = ({ s, i }) => (
    <div role="group" aria-label="발화 도구" style={{
      position: 'absolute',
      top: '-14px',
      right: '36px',
      display: 'flex',
      gap: '2px',
      padding: '2px',
      background: '#fff',
      border: '1px solid #e5e5e2',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(15,23,42,.12)',
      zIndex: 4,
      opacity: (hoverSeg === i || focusSeg === i) ? 1 : 0,
      transition: 'opacity .12s',
    }}>
      {segMarkButtons(s, i, segToolBtn)}
    </div>
  );

  const segMenuItem = {
    width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    padding: '7px 9px',
    borderRadius: '7px',
    border: '1px solid transparent',
    background: '#fff',
    color: '#64635f',
    fontWeight: 500,
    cursor: 'pointer',
  };

  const SegmentMenu = ({ s, i }) => {
    // 표식 강조는 모바일에서만 — 데스크톱은 표식 버튼이 ⋮ 밖(도구 줄)에 있어 오해를 부른다.
    const activeMark = mobile && !!(s.bookmark || s.highlight || (s.memo || '').trim());
    const box = segMenuBox && segMenuBox.i === i ? segMenuBox : null;
    return (
      <details data-seg-menu="1" onToggle={(e) => onSegMenuToggle(e, i)} style={{ position: 'relative', flexShrink: 0 }}>
        <summary title="발화 작업" aria-label="발화 작업" style={{
          ...iconButtonStyle(activeMark ? T.color.selectedText : '#64635f'),
          width: '28px',
          height: '28px',
          listStyle: 'none',
          background: activeMark ? T.color.selectedBg : '#fff',
        }}>
          <IconDotsVertical size={16} />
        </summary>
        {/* 🔴 위로 열 때는 `bottom` 만 둔다 — `top: 'auto'` 를 함께 두면 환경에 따라 32px 이 남는다. */}
        <div data-seg-menu-panel="1" style={{
          position: 'absolute',
          right: 0,
          ...(box?.up ? { bottom: '32px' } : { top: '32px' }),
          ...(box?.maxH ? { maxHeight: `${box.maxH}px`, overflowY: 'auto' } : null),
          zIndex: 12,
          minWidth: '132px',
          padding: '5px',
          border: '1px solid #e5e5e2',
          borderRadius: '8px',
          background: '#fff',
          boxShadow: '0 10px 24px rgba(15,23,42,.14)',
        }}>
          {mobile && segMarkButtons(s, i, segActionBtn)}
          {mobile && <div role="separator" style={{ height: '1px', margin: '4px 2px', background: '#e5e5e2' }} />}
          <button onClick={(e) => { closeDetailMenu(e); copySegmentText(s); }} style={segMenuItem}>텍스트 복사</button>
          <button onClick={(e) => { closeDetailMenu(e); startEdit(); }} style={segMenuItem}>음성 기록 편집</button>
        </div>
      </details>
    );
  };

  // 메모 추가하기 — 지금 재생 시각에 녹음 메모 한 줄을 남긴다.
  // 🔴 함수 호출(`{MemoAddBox()}`)로만 쓴다 — 컴포넌트 태그로 쓰면 글자마다 입력칸이 다시
  //    마운트되어 포커스와 한글 조합이 깨진다(SegmentMenu 와 같은 함정).
  const MemoAddBox = () => (
    <div style={cardStyle}>
      <textarea
        value={memoDraft}
        onChange={(e) => setMemoDraft(e.target.value)}
        onKeyDown={memoDraftKeyDown}
        disabled={memoLocked || notesFull}
        rows={2}
        maxLength={RECORDING_NOTE_TEXT_MAX}
        aria-label="메모 추가"
        placeholder="메모 추가하기"
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: `1px solid ${T.color.border}`, borderRadius: '8px', background: (memoLocked || notesFull) ? T.color.subtle : '#fff', padding: '9px 10px', fontFamily: 'inherit', fontSize: '14px', lineHeight: 1.6, color: '#32312e', minHeight: '56px' }}
      />
      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: T.color.muted, fontVariantNumeric: 'tabular-nums' }}>{timeText(memoTime)}에 추가</span>
        <span style={{ fontSize: '11px', color: T.color.faint, fontWeight: 700 }}>{recordingNotes.length}/{RECORDING_NOTES_MAX}</span>
        <button type="button" onClick={() => addRecordingMemo(memoDraft)}
          disabled={!memoDraft.trim() || memoLocked || notesFull}
          style={{ ...smBtn(T.color.green), marginLeft: 'auto', opacity: (!memoDraft.trim() || memoLocked || notesFull) ? 0.55 : 1 }}>
          {notesSaving ? '저장 중…' : '추가'}
        </button>
      </div>
      {notesFull && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#b45309', lineHeight: 1.5 }}>메모는 {RECORDING_NOTES_MAX}개까지 남길 수 있습니다.</div>
      )}
      {editing && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#92918d', lineHeight: 1.5 }}>편집을 마친 뒤 메모를 추가할 수 있습니다.</div>
      )}
    </div>
  );

  const RecordingNotesCard = () => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 800, color: '#32312e', marginBottom: '10px' }}>
        <span>녹음 메모</span>
        <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '999px', background: '#ecfdf5', color: '#047857' }}>{recordingNotes.length}</span>
      </div>
      {recordingNotes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* 🔴 id·scrollMarginTop 은 머리 검색의 이동 대상이다(마크다운이라 <mark> 는 칠하지 않고 깜빡임만 준다). */}
          {recordingNotes.map((n, i) => (
            <div key={`${n.time}-${i}`} id={`mnote-${i}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', lineHeight: 1.5, scrollMarginTop: jumpScrollMarginTop, ...jumpPulseStyle(`mnote-${i}`) }}>
              <button onClick={() => seekTo(n.time)} disabled={!audioUrl}
                style={{ flexShrink: 0, minWidth: '52px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: audioUrl ? T.color.selectedText : '#64635f', background: 'none', border: 'none', cursor: audioUrl ? 'pointer' : 'default', padding: 0, fontSize: '13px', fontWeight: 800 }}>
                {timeText(n.time)}
              </button>
              <div style={{ flex: 1, minWidth: 0, color: '#32312e', ...softWrap }}><Markdown compact>{mdHardBreaks(n.text)}</Markdown></div>
              {/* 🔴 보이는 «삭제» 글자를 쓰지 않는다(바깥 QA 스크립트가 글자로 노트 삭제를 찾는다). */}
              {canEditNotes && (
                <button type="button" onClick={() => removeRecordingMemo(i)} disabled={memoLocked}
                  aria-label="녹음 메모 삭제" title="삭제"
                  style={{ flexShrink: 0, width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '6px', background: 'transparent', color: T.color.faint, cursor: memoLocked ? 'not-allowed' : 'pointer', opacity: memoLocked ? 0.45 : 1 }}>
                  <IconX size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: '#92918d', lineHeight: 1.5 }}>녹음 메모가 없습니다.</div>
      )}
    </div>
  );

  // 발화 메모 모아보기 — 시각(seekTo) · 화자 이름 · 메모 글 · [수정](editSegMemo)
  const SegMemosCard = () => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 800, color: '#32312e', marginBottom: '10px' }}>
        <span>발화 메모</span>
        <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '999px', background: '#eaf3fc', color: '#1873ce' }}>{segMemos.length}</span>
      </div>
      {segMemos.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {segMemos.map(({ s, i }) => (
            <div key={`segmemo-${i}`} id={`msegmemo-${i}`}
              style={{ display: 'flex', flexDirection: 'column', gap: '4px', scrollMarginTop: jumpScrollMarginTop, ...jumpPulseStyle(`msegmemo-${i}`) }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => seekTo(s.start)} disabled={!audioUrl} title={`${timeText(s.start)} 재생`}
                  style={{ flexShrink: 0, minWidth: '52px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: audioUrl ? T.color.selectedText : '#64635f', background: 'none', border: 'none', cursor: audioUrl ? 'pointer' : 'default', padding: 0, fontSize: '13px', fontWeight: 800 }}>
                  {timeText(s.start)}
                </button>
                {/* 🔴 spkDisp 는 함수가 아니라 이름 «맵» 이다 — dispSpk(speaker, spkDisp) 로만 읽는다. */}
                {!hideSpeakers && s.speaker && (
                  <span style={{ minWidth: 0, fontSize: '12px', fontWeight: 800, color: '#64635f', ...softWrap }}>{dispSpk(s.speaker, spkDisp)}</span>
                )}
                <button type="button" onClick={() => editSegMemo(i)} disabled={editing || markSaving}
                  aria-label="발화 메모 수정"
                  style={{ marginLeft: 'auto', flexShrink: 0, fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', border: `1px solid ${T.color.border}`, background: '#fff', color: T.color.muted, cursor: (editing || markSaving) ? 'not-allowed' : 'pointer', opacity: (editing || markSaving) ? 0.55 : 1 }}>
                  수정
                </button>
              </div>
              <div style={{ padding: '7px 9px', background: '#fbfbfa', border: `1px solid ${T.color.border}`, borderRadius: '8px', color: '#64635f', fontSize: '13px', whiteSpace: 'pre-wrap', ...softWrap }}>
                {hasSearch ? renderTranscriptSearchText((s.memo || '').trim(), searchNeedle) : (s.memo || '').trim()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: '#92918d', lineHeight: 1.5 }}>발화 메모가 없습니다.</div>
      )}
    </div>
  );

  // ── 모아보기 판(클로바 묶음 D2a) ───────────────────────────────────────────
  // 머리 «모아보기» 아이콘을 누르면 오른쪽 패널(요약·메모)이 이 판으로 바뀐다.
  const collectLabel = COLLECT_TABS.find((t) => t.key === collectTab)?.label || '';
  // 판 안이 화면을 넘지 않게 두는 아래 여백 = <main> 아래 여백 + (재생바가 있으면) 상세 상자 아래 여백.
  const collectBottomGap = COLLECT_MAIN_PAD + (hasBottomAudio ? COLLECT_PLAYBAR_H : 0);
  // ✕ 해제 — 표식을 끄고, 사라질 행 자리를 기억해 둔다(포커스는 저장이 끝난 뒤 옮긴다).
  const unmarkCollect = (i, pos) => {
    if (markSaving || editing) return;
    collectFocusRef.current = { tab: collectTab, pos };
    toggleSegFlag(i, collectTab);
  };
  const collectTabStyle = (on) => ({
    border: 'none',
    borderRadius: T.radius.sm,
    background: on ? T.color.surface : 'transparent',
    color: on ? T.color.selectedText : T.color.muted,
    fontSize: T.font.sm,
    fontWeight: 800,
    padding: '6px 12px',
    cursor: 'pointer',
    boxShadow: on ? T.shadow.card : 'none',
  });
  // 🔴 함수 호출(`{CollectCard()}`)로만 쓴다 — 컴포넌트 태그로 쓰면 렌더마다 새 타입이라 통째로
  //    다시 마운트된다(MemoAddBox·SegmentMenu 와 같은 함정).
  const CollectCard = () => (
    <div ref={collectPanelRef} style={{
      ...cardStyle,
      // 두 열일 때는 그리드 항목인 <aside> 가 sticky 다(아래) → 판은 화면 높이 안으로만 자라게 둔다.
      // 🔴 100vh 가 아니라 --gw-app-h — 분할 화면·모바일 키보드가 그 변수로 높이를 내려 준다.
      ...(stacked
        ? { scrollMarginTop: `${collectHeadH + COLLECT_GAP}px` }
        : {
          maxHeight: `calc(var(--gw-app-h, 100dvh) - ${collectHeadH}px - ${COLLECT_GAP}px - ${collectBottomGap}px - ${COLLECT_GAP}px)`,
          overflowY: 'auto',
        }),
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 800, color: '#32312e' }}>모아보기</div>
        <button type="button" onClick={closeCollect} aria-label="모아보기 닫기" title="닫기"
          style={{ flexShrink: 0, width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '6px', background: 'transparent', color: T.color.muted, cursor: 'pointer' }}>
          <IconX size={16} />
        </button>
      </div>
      <div role="tablist" aria-label="모아보기"
        style={{ display: 'inline-flex', maxWidth: '100%', padding: '3px', borderRadius: T.radius.md, background: T.color.subtle, border: `1px solid ${T.color.border}`, gap: '3px' }}>
        {COLLECT_TABS.map((t) => {
          const on = collectTab === t.key;
          return (
            <button key={t.key} type="button" role="tab" id={`collect-tab-${t.key}`}
              aria-controls={`collect-panel-${t.key}`} aria-selected={on}
              data-testid={`collect-tab-${t.key}`} data-count={markCounts[t.key]}
              onClick={() => setCollectTab(t.key)} style={collectTabStyle(on)}>
              {t.label}
              {/* 개수는 눈으로만 — 접근 이름은 정확히 «북마크»·«하이라이트» 로 남긴다. */}
              <span aria-hidden="true"> {markCounts[t.key]}</span>
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`collect-panel-${collectTab}`} aria-labelledby={`collect-tab-${collectTab}`}
        data-testid={`collect-panel-${collectTab}`} style={{ marginTop: '12px' }}>
        {collectItems.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {collectItems.map(({ s, i }, pos) => (
              <div key={`collect-${collectTab}-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button type="button" onClick={() => seekTo(s.start)} disabled={!audioUrl} title={`${timeText(s.start)} 재생`}
                    style={{ flexShrink: 0, minWidth: '52px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: audioUrl ? T.color.selectedText : '#64635f', background: 'none', border: 'none', cursor: audioUrl ? 'pointer' : 'default', padding: 0, fontSize: '13px', fontWeight: 800 }}>
                    {timeText(s.start)}
                  </button>
                  {/* 🔴 spkDisp 는 함수가 아니라 이름 «맵» 이다 — dispSpk(speaker, spkDisp) 로만 읽는다. */}
                  {!hideSpeakers && s.speaker && (
                    <span style={{ minWidth: 0, fontSize: '12px', fontWeight: 800, color: '#64635f', ...softWrap }}>{dispSpk(s.speaker, spkDisp)}</span>
                  )}
                  <button type="button" data-collect-unmark="1" onClick={() => unmarkCollect(i, pos)}
                    disabled={markSaving || editing} aria-label={`${collectLabel} 해제`} title={`${collectLabel} 해제`}
                    style={{ marginLeft: 'auto', flexShrink: 0, width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '6px', background: 'transparent', color: T.color.faint, cursor: (markSaving || editing) ? 'not-allowed' : 'pointer', opacity: (markSaving || editing) ? 0.45 : 1 }}>
                    <IconX size={14} />
                  </button>
                </div>
                {/* 발화 글 — 누르면 전사의 그 발화로 간다. 🔴 버튼이라야 키보드로 닿는다(글자 크기는 font: inherit). */}
                <button type="button" onClick={() => scrollToSeg(i)}
                  style={{ width: '100%', font: 'inherit', fontSize: '13px', lineHeight: 1.5, textAlign: 'left', border: 'none', background: 'none', padding: 0, margin: 0, color: '#64635f', cursor: 'pointer', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', ...softWrap }}>
                  {s.text}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#92918d', lineHeight: 1.5 }}>
            {COLLECT_TABS.find((t) => t.key === collectTab)?.empty}
          </div>
        )}
      </div>
    </div>
  );

  // ── 요약 종류 «AI 맞춤 요약 | 시간대별 요약»(클로바 묶음 E2) ──────────────────
  const SUMMARY_KINDS = [
    { key: 'ai', label: 'AI 맞춤 요약' },
    { key: 'time', label: '시간대별 요약' },
  ];
  const chapters = Array.isArray(active?.chapters) ? active.chapters : [];
  // 요약 생성과 챕터 만들기는 서버에서 같은 잠금이다 — 서버 잠금표가 프로세스 안 메모리라 화면이 주 방어선이다.
  const chaptersOff = chaptersBusy || summarizing || editing || active?.status !== 'done';
  // 부를 때의 노트 id 를 붙들어 둔다 — 그 사이 다른 노트로 바꿨으면 결과를 새 노트에 넣지 않는다.
  const genChapters = async () => {
    const id = active?.id;
    if (id == null) return;
    setChaptersBusyId(id);
    try {
      const { ok, data } = await jsonFetch(`/api/meetings/${id}/chapters`, { method: 'POST' });
      if (ok) {
        // chapters_falsify_warn: 반증 ⚠ 줄(F2c-2a · 챕터 밖 · 저장 안 함) — 다시 만들면 이전 줄을 덮는다.
        setActive((a) => (a && a.id === id ? { ...a, chapters: (data && data.chapters) || [], chapters_falsify_warn: (data && data.falsify_warn) || '' } : a));
      } else {
        alertDialog('시간대별 요약 실패: ' + detailOr(data, '만들지 못했습니다.'));
      }
    } catch (e) {
      alertDialog('시간대별 요약 실패: ' + detailOr(null, '만들지 못했습니다.'));
    } finally {
      setChaptersBusyId((cur) => (cur === id ? null : cur));
    }
  };
  const chaptersBtn = (label) => (
    <button type="button" onClick={genChapters} disabled={chaptersOff}
      style={{ ...smBtn(T.color.green), opacity: chaptersOff ? 0.6 : 1 }}>
      {chaptersBusy ? '만드는 중…' : label}
    </button>
  );
  const ChaptersBody = () => (
    <div data-testid="summary-chapters">
      <div style={{ fontSize: '12px', color: '#64635f', marginBottom: '10px', lineHeight: 1.5, padding: '8px 10px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '8px' }}>
        AI가 음성 기록을 요약한 결과로, 부정확하거나 부적절한 내용이 포함될 수 있습니다.
      </div>
      {chapters.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', minWidth: 0, fontSize: T.font.base, color: T.color.faint }}>시간대별 요약이 없습니다.</div>
          {chaptersBtn('시간대별 요약 만들기')}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            {chaptersBtn('다시 만들기')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {chapters.map((c, i) => {
              const range = `${timeText(c.start)}~${timeText(c.end)}`;
              const points = Array.isArray(c.points) ? c.points : [];
              return (
                <div key={`${c.start}-${i}`} data-testid={`mchapter-${i}`}>
                  <button type="button" onClick={() => seekTo(c.start)} disabled={!audioUrl} aria-label={`${range} 재생`} title={`${range} 재생`}
                    style={{ fontSize: '12px', fontWeight: 800, color: audioUrl ? T.color.selectedText : '#92918d', background: 'none', border: 'none', padding: 0, cursor: audioUrl ? 'pointer' : 'default', fontVariantNumeric: 'tabular-nums' }}>
                    {range}
                  </button>
                  <div style={{ fontSize: T.font.md, fontWeight: 900, color: T.color.textStrong, marginTop: '3px', ...softWrap }}>{c.title}</div>
                  {points.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {points.map((p, j) => (
                        <li key={j} style={{ fontSize: T.font.base, lineHeight: 1.55, color: T.color.text, ...softWrap }}>{p}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
          {/* 반증 ⚠ 줄(F2c-2a) — 챕터 목록 아래 · 저장 안 함 · 문구는 서버 값 그대로(새 아이콘 금지).
              묶음마다 한 줄이라 여러 줄이 \n 으로 온다 → 이 줄만 pre-line(softWrap 은 줄바꿈을 살리지 않는다). */}
          {active.chapters_falsify_warn && (
            <div role="note" data-testid="chapters-falsify-warn" style={{ marginTop: '12px', fontSize: '12px', lineHeight: 1.5, color: '#b45309', padding: '6px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', ...softWrap, whiteSpace: 'pre-line' }}>
              {active.chapters_falsify_warn}
            </div>
          )}
        </>
      )}
    </div>
  );

  const SummaryCard = () => (
    <div style={cardStyle}>
      {/* 🔴 tablist 이름에 «요약 양식»·«요약과 메모» 를 쓰지 않는다 — 시험·e2e 가 그 이름으로 찾는다. */}
      <div style={{ marginBottom: '12px' }}>
        <div role="tablist" aria-label="요약 종류"
          style={{ display: 'inline-flex', maxWidth: '100%', padding: '3px', borderRadius: T.radius.md, background: T.color.subtle, border: `1px solid ${T.color.border}`, gap: '3px' }}>
          {SUMMARY_KINDS.map((t) => {
            const on = sumKind === t.key;
            return (
              <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => setSumKind(t.key)}
                style={{ border: 'none', borderRadius: T.radius.sm, background: on ? T.color.surface : 'transparent', color: on ? T.color.selectedText : T.color.muted, fontSize: T.font.sm, fontWeight: 800, padding: '6px 12px', cursor: 'pointer', boxShadow: on ? T.shadow.card : 'none' }}>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {sumKind === 'time' ? ChaptersBody() : (
      <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <div style={{ fontSize: T.font.lg, fontWeight: 900, color: T.color.textStrong }}>AI 요약</div>
          <div style={{ fontSize: T.font.xs, color: T.color.muted, marginTop: '3px' }}>마크다운 렌더링 · DOCX 다운로드</div>
        </div>
        {showControls && (
          <div role='tablist' aria-label='요약 양식' style={{ display: 'inline-flex', padding: '3px', borderRadius: T.radius.md, background: T.color.subtle, border: `1px solid ${T.color.border}`, gap: '3px', maxWidth: '100%' }}>
            {summaryModes.map((mode) => {
              const on = sumMode === mode.value;
              return (
                <button key={mode.value} type='button' role='tab' aria-selected={on} onClick={() => setSumMode(mode.value)}
                  style={{ border: 'none', borderRadius: T.radius.sm, background: on ? T.color.surface : 'transparent', color: on ? T.color.selectedText : T.color.muted, fontSize: T.font.xs, fontWeight: 800, padding: '5px 8px', cursor: 'pointer', boxShadow: on ? T.shadow.card : 'none' }}>
                  {mode.label}
                </button>
              );
            })}
          </div>
        )}
        <button onClick={handleGenSummary} disabled={summarizing || chaptersBusy} style={{ ...smBtn(T.color.green), opacity: (summarizing || chaptersBusy) ? 0.6 : 1 }}>
          {summarizing ? '생성 중…' : (active.summary ? '다시 요약' : '요약 생성')}
        </button>
        {/* 요약 받기 형식(MD·TXT·DOCX)을 고르는 «다운로드 ▾» 메뉴(2026-10-15 · 예전 «DOCX» 단추 자리).
            🔴 closeOtherMenus 는 붙이지 않는다 — 같은 부모 안 형제만 닫는데 도구줄 메뉴와 부모가 다르다. */}
        {active.summary && (
          <details style={{ position: 'relative' }}>
            <summary title="요약 다운로드" aria-label="요약 다운로드"
              style={{ ...smBtn(T.color.slate), display: 'inline-flex', alignItems: 'center', gap: '5px', listStyle: 'none' }}>
              <IconDownload size={15} />다운로드 ▾
            </summary>
            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 20, minWidth: '160px', padding: '5px', border: '1px solid #e5e5e2', borderRadius: '8px', background: '#fff', boxShadow: '0 10px 24px rgba(15,23,42,.14)' }}>
              <button onClick={(e) => { closeDetailMenu(e); dlSummary(active, 'md'); }} aria-label="요약 MD 다운로드" style={menuItemStyle}><IconFileText size={16} />Markdown (.md)</button>
              <button onClick={(e) => { closeDetailMenu(e); dlSummary(active, 'txt'); }} aria-label="요약 TXT 다운로드" style={menuItemStyle}><IconFileText size={16} />텍스트 (.txt)</button>
              <button onClick={(e) => { closeDetailMenu(e); dlSummary(active, 'docx'); }} aria-label="요약 DOCX 다운로드" style={menuItemStyle}><IconFileText size={16} />Word (.docx)</button>
            </div>
          </details>
        )}
        {hasSummaryVersions && (
          <button onClick={openSummaryVersions} disabled={versionsLoading} style={{ ...smBtn(T.color.slate), opacity: versionsLoading ? 0.6 : 1 }}>버전 ▾</button>
        )}
        {active.summary && !sumOpen && (
          <button onClick={() => setSumOpen(true)} disabled={summarizing} style={{ ...smBtn(T.color.slate), opacity: summarizing ? 0.6 : 1 }}>양식</button>
        )}
        {active.summary && sumOpen && (
          <button onClick={() => setSumOpen(false)} style={smBtn(T.color.slate)}>접기</button>
        )}
      </div>
      {versionsOpen && hasSummaryVersions && (
        <div style={{ marginBottom: '12px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: '#fff', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px', borderBottom: `1px solid ${T.color.border}`, background: '#fbfbfa' }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: T.font.sm, fontWeight: 900, color: T.color.textStrong }}>요약 버전</div>
            <button type='button' title='닫기' aria-label='닫기' onClick={() => setVersionsOpen(false)}
              style={{ border: 'none', background: 'transparent', padding: '2px', color: T.color.muted, cursor: 'pointer', display: 'inline-flex' }}>
              <IconX size={15} />
            </button>
          </div>
          {versionsError && <div style={{ padding: '8px 10px', fontSize: T.font.xs, color: '#b91c1c', background: '#fef2f2' }}>{versionsError}</div>}
          {versionsLoading && <div style={{ padding: '8px 10px', fontSize: T.font.xs, color: T.color.muted }}>불러오는 중입니다.</div>}
          <div style={{ maxHeight: '220px', overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {summaryVersions.map((version) => {
              const on = selectedSummaryVersion?.id === version.id;
              return (
                <button key={version.id} type='button' onClick={() => setSelectedSummaryVersionId(version.id)}
                  style={{ width: '100%', textAlign: 'left', border: `1px solid ${on ? '#d3e7f9' : '#e5e5e2'}`, borderRadius: T.radius.sm, background: on ? '#eaf3fc' : '#fff', padding: '8px 9px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: T.font.xs, fontWeight: 900, color: on ? '#1873ce' : T.color.textStrong }}>{fmtSummaryVersionDate(version.created_at) || '-'}</span>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: on ? '#1873ce' : T.color.muted, padding: '2px 6px', borderRadius: '999px', background: on ? '#eaf3fc' : '#f1f1ef' }}>{summaryModeLabel(version.mode)}</span>
                  </div>
                  <div style={{ fontSize: T.font.xs, lineHeight: 1.45, color: T.color.muted, ...softWrap }}>{version.preview || '내용 없음'}</div>
                </button>
              );
            })}
          </div>
          {selectedSummaryVersion && (
            <div style={{ padding: '9px 10px', borderTop: `1px solid ${T.color.border}`, background: '#fbfbfa' }}>
              <div style={{ fontSize: T.font.xs, color: T.color.text, lineHeight: 1.5, marginBottom: '8px', ...softWrap }}>{selectedSummaryVersion.preview || '미리보기 없음'}</div>
              <button type='button' onClick={restoreSummaryVersion} disabled={restoringSummary} style={{ ...smBtn(T.color.green), opacity: restoringSummary ? 0.6 : 1 }}>
                {restoringSummary ? '복원 중' : '이 버전으로 복원'}
              </button>
            </div>
          )}
        </div>
      )}
      {showControls && active.summary_prompt_used && <div style={{ fontSize: '11px', color: '#92918d', marginBottom: '10px', ...softWrap }}>{active.summary_prompt_used}</div>}
      {showControls && (
        <>
          {sumMode === 'default' && (
            <div style={{ fontSize: '12px', color: '#64635f', marginBottom: '10px', lineHeight: 1.5, padding: '8px 10px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '8px' }}>
              기본 양식: <b>안건</b> · <b>결정 사항</b> · <b>액션 아이템</b>[담당자·기한] · <b>요약</b>(3~5문장) — 마크다운으로 정리됩니다.
            </div>
          )}
          {sumMode === 'folder' && (
            <div title={folderPromptNotice.title} style={{ fontSize: '12px', color: folderPromptNotice.warning ? '#b45309' : '#64635f', marginBottom: '10px', lineHeight: 1.5, padding: '8px 10px', background: folderPromptNotice.warning ? '#fff7ed' : '#fbfbfa', border: `1px solid ${folderPromptNotice.warning ? '#fed7aa' : '#e5e5e2'}`, borderRadius: '8px' }}>
              {folderPromptNotice.text}
            </div>
          )}
          {sumMode === 'custom' && (
            <textarea value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="예: 액션 아이템만 표로 정리 / 영어로 요약 / 3줄 요약"
              style={{ width: '100%', minHeight: '50px', fontSize: '13px', padding: '8px', borderRadius: '8px', border: '1px solid #e5e5e2', marginBottom: '10px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          )}
        </>
      )}
      {actionItems.length > 0 && (
        <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: T.radius.md }}>
          <div style={{ fontSize: T.font.sm, fontWeight: 900, color: '#64635f', marginBottom: '6px' }}>할 일</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {actionItems.map((item, i) => (
              <div key={i} style={{ fontSize: T.font.base, lineHeight: 1.45, color: T.color.text, ...softWrap }}>{item}</div>
            ))}
          </div>
        </div>
      )}
      {active.summary
        ? (
          // 🔴 id·scrollMarginTop 은 머리 검색의 이동 대상이다. 마크다운으로 그리므로 <mark> 로
          //    칠하지 않고(평문으로 바꾸면 서식이 사라진다) 깜빡임으로만 자리를 보인다.
          <div id="msummary" style={{ fontSize: T.font.md, lineHeight: 1.6, color: T.color.text, ...softWrap, scrollMarginTop: jumpScrollMarginTop, ...jumpPulseStyle('msummary') }}>
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkStrong]} components={mdComponents}>{active.summary}</ReactMarkdown>
          </div>
        )
        : (
          // 요약 전 안내 상자(클로바 묶음 E · H1). 배경·테두리·radius 는 위 «기본 양식» 안내 박스와 같은 값이다.
          // 🔴 onClick 은 handleGenSummary 다 — genSummary 를 바로 걸면 이벤트 객체가 scope 로 들어가
          //    긴 전사 선택 모달을 건너뛰고 그 객체가 API 로 간다.
          // 🔴 aria-label «요약하기» 는 생성 중에도 고정한다 — 빼면 이름 «생성 중…» 버튼이 둘이 되고(e2e 가
          //    그 이름으로 머리 버튼을 본다), «요약 생성» 으로 지으면 머리 버튼과 이름이 겹친다.
          <div style={{ textAlign: 'center', padding: '16px 12px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '8px' }}>
            <div style={{ fontSize: T.font.md, fontWeight: 800, color: T.color.textStrong, marginBottom: '4px', ...softWrap }}>AI가 요약한 핵심 내용을 확인해보세요</div>
            <div style={{ fontSize: T.font.sm, color: T.color.faint, marginBottom: '10px', ...softWrap }}>아직 요약이 없습니다. 양식을 고르고 요약 생성을 누르십시오.</div>
            <button type="button" aria-label="요약하기" onClick={handleGenSummary} disabled={summarizing || chaptersBusy} style={{ ...smBtn(T.color.green), opacity: (summarizing || chaptersBusy) ? 0.6 : 1 }}>
              {summarizing ? '생성 중…' : '✦ 요약'}
            </button>
          </div>
        )}
      {/* 반증 ⚠ 한 줄(F2c-1) — 요약 본문 밖 · 저장 안 함 · 문구는 서버 값 그대로(⚠ 는 서버 문구에 있다 — 새 아이콘 금지). */}
      {active.summary && active.summary_falsify_warn && (
        <div role="note" data-testid="summary-falsify-warn" style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5, color: '#b45309', padding: '6px 10px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', ...softWrap }}>
          {active.summary_falsify_warn}
        </div>
      )}
      </>
      )}
    </div>
  );

  const TranscriptPanel = () => (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#32312e' }}>{editing ? '음성 기록 편집' : '음성 기록'}</div>
          <div style={{ fontSize: '12px', color: '#64635f', marginTop: '2px' }}>{active.speaker_count}명 · {fmtDur(active.duration_sec) || '0:00'}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {!editing
            ? (
              <>
                <button onClick={startEdit} style={smBtn('#64635f')}>편집</button>
                <button onClick={copyTranscript} style={smBtn(copyOk ? '#16a34a' : '#64635f')}>{copyOk ? '복사됨' : '복사'}</button>
              </>
            )
            : (
              // 클로바 머리 = «음성 기록 편집 [완료][취소][복사]» — [완료] 는 옛 [저장] 과 같은 핸들러다.
              <>
                <button onClick={handleSaveEdit} disabled={saving} style={{ ...smBtn('#16a34a'), opacity: saving ? 0.6 : 1 }}>{saving ? '저장 중…' : '완료'}</button>
                <button onClick={() => setEditing(false)} style={smBtn('#64635f')}>취소</button>
                <button onClick={copyEditTranscript} style={smBtn(copyOk ? '#16a34a' : '#64635f')}>{copyOk ? '복사됨' : '복사'}</button>
              </>
            )}
        </div>
      </div>
      {/* 🔴 전사 위 검색 줄은 머리 아래 전체 폭 줄로 옮겼다(클로바 묶음 D2b) — 전사뿐 아니라
          요약·메모·참석자까지 찾으므로 패널 안이 아니라 머리 «검색» 아이콘으로 연다.
          🔴 전사 위 «전체/북마크/하이라이트/메모» 필터 탭은 없앴다(클로바 묶음 D2a) —
          북마크·하이라이트는 머리 «모아보기» 판, 메모는 오른쪽 «메모» 탭에서 본다. */}

      {!editing ? (
        transcript.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {transcriptGroups.length > 0 ? transcriptGroups.map((group) => {
              const first = group.items[0].s;
              const speakerName = first.speaker ? dispSpk(first.speaker, spkDisp) : '화자 없음';
              return (
                <div key={`group-${group.items[0].i}`} style={{ padding: '2px 0 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    {/* ⋮ «참석자 숨기기» 는 화자 이름·아바타만 감춘다 — 🔴 아래 시각 버튼(e2e 가 쓴다)은 남는다. */}
                    {!hideSpeakers && <SpeakerAvatar speaker={first.speaker} />}
                    {!hideSpeakers && <span style={{ fontSize: '13px', fontWeight: 800, color: '#32312e', minWidth: 0, ...softWrap }}>{speakerName}</span>}
                    <button onClick={() => seekTo(first.start)} title="이 지점 재생" disabled={!audioUrl}
                      style={{ fontSize: '12px', color: audioUrl ? T.color.selectedText : '#92918d', background: 'none', border: 'none', cursor: audioUrl ? 'pointer' : 'default', padding: 0, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                      {timeText(first.start)}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: mobile ? 0 : '36px' }}>
                    {group.items.map(({ s, i }) => {
                      const playing = audioUrl && playSec >= s.start && playSec < (s.end || s.start + 1);
                      const memo = (s.memo || '').trim();
                      // 찾은 곳이 있는 발화는 검색 칠하기 그대로 · 재생 위치가 든 발화만 지금 낱말을 칠한다(S4).
                      // 🔴 검색이 못 찾으면 renderTranscriptSearchText 가 문자열이 아니라 [raw] 를 돌려준다 →
                      //    «찾은 곳» 은 searchHit 로 따진다. 못 칠하면 지금과 같은 값.
                      const plainText = hasSearch ? renderTranscriptSearchText(s.text, searchNeedle) : s.text;
                      const bodyText = playing && !(hasSearch && searchHit(s.text))
                        ? (renderPlayingWordText(s.text, s.words, playSec) || plainText)
                        : plainText;
                      return (
                        <div key={i}>
                          <div id={`mseg-${i}`} data-testid={`mseg-${i}`}
                            onMouseEnter={mobile ? undefined : () => setHoverSeg(i)}
                            onMouseLeave={mobile ? undefined : () => setHoverSeg((cur) => (cur === i ? null : cur))}
                            onFocus={mobile ? undefined : (e) => { if (isKeyboardFocus(e.target)) setFocusSeg(i); }}
                            onBlur={mobile ? undefined : (e) => {
                              // 🔴 행 안의 다른 요소로 옮겨 가는 포커스는 유지한다. relatedTarget 이 없거나
                              //    요소가 아니면(jsdom 은 Document 를 넘긴다) 행을 떠난 것으로 본다.
                              const rt = e.relatedTarget;
                              if (rt && rt.nodeType === 1 && e.currentTarget.contains(rt)) return;
                              setFocusSeg((cur) => (cur === i ? null : cur));
                            }}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                              fontSize: '14px',
                              lineHeight: 1.58,
                              background: playing ? '#fef08a' : (s.highlight ? '#fff7ed' : '#fff'),
                              borderRadius: '8px',
                              borderLeft: s.bookmark ? '3px solid #2563eb' : (s.highlight ? '3px solid #f59e0b' : '3px solid transparent'),
                              padding: '7px 7px 7px 10px',
                              boxShadow: jumpPulseIndex === i ? '0 0 0 2px #2563eb inset' : 'none',
                              transition: 'background .15s, box-shadow .15s',
                            }}>
                            <button onClick={() => seekTo(s.start)} disabled={!audioUrl} title={`${timeText(s.start)} 재생`}
                              style={{ flexShrink: 0, fontSize: '11px', fontWeight: 700, color: audioUrl ? T.color.selectedText : '#92918d', background: 'none', border: 'none', padding: '1px 0', cursor: audioUrl ? 'pointer' : 'default', fontVariantNumeric: 'tabular-nums', minWidth: '38px', textAlign: 'left', lineHeight: 1.58 }}>
                              {timeText(s.start)}
                            </button>
                            <button onClick={() => seekTo(s.start)} disabled={!audioUrl} title={`${timeText(s.start)} 재생`}
                              style={{ flex: 1, minWidth: 0, textAlign: 'left', color: '#32312e', whiteSpace: 'pre-wrap', background: 'transparent', border: 'none', padding: 0, cursor: audioUrl ? 'pointer' : 'default', font: 'inherit', ...softWrap }}>
                              {bodyText}
                            </button>
                            {/* 🔴 컴포넌트 태그(<SegmentMenu/>)로 쓰면 렌더마다 새 타입이라 통째로 다시
                                마운트된다 — hover 가 바뀔 때마다 열린 ⋮ 가 닫히고 포커스가 사라졌다. */}
                            {!mobile && SegmentTools({ s, i })}
                            {SegmentMenu({ s, i })}
                          </div>
                          {memo && (
                            <div style={{ margin: '4px 42px 0 10px', padding: '7px 9px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '8px', color: '#64635f', fontSize: '13px', whiteSpace: 'pre-wrap', ...softWrap }}>
                              {hasSearch ? renderTranscriptSearchText(memo, searchNeedle) : memo}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }) : (
              <div style={{ color: '#92918d', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>선택한 표시가 없습니다.</div>
            )}
          </div>
        ) : (
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '14px', fontFamily: 'inherit', color: '#32312e', ...softWrap }}>{active.transcript_text || '(변환 내용 없음)'}</pre>
        )
      ) : (
        <div>
          {Object.keys(spkMap).length > 0 && (
            <div style={{ marginBottom: '12px', padding: '10px', background: '#fbfbfa', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#64635f', marginBottom: '6px' }}>화자 이름 변경(전체 일괄)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.keys(spkMap).map((orig) => (
                  <div key={orig} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontSize: '11px', color: '#92918d' }}>{dispSpk(orig, spkDisp)} →</span>
                    <input value={spkMap[orig]} onChange={(e) => setSpkMap((m) => ({ ...m, [orig]: e.target.value }))}
                      style={{ width: '100px', fontSize: '12px', padding: '3px 6px', borderRadius: '6px', border: '1px solid #e5e5e2' }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px', padding: '8px 10px', background: '#fbfbfa', border: '1px solid #e5e5e2', borderRadius: '8px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 800, color: '#64635f' }}>
              <input type="checkbox" checked={allEditSelected} onChange={toggleAllEditSelection} disabled={editSegs.length === 0} />
              전체선택
            </label>
            <span style={{ fontSize: '12px', color: '#64635f', fontWeight: 800 }}>선택 {selectedEditCount}개</span>
            <button type="button" onClick={() => setSelectedEditIndexes([])} disabled={selectedEditCount === 0} style={{ ...smBtn('#64635f'), opacity: selectedEditCount === 0 ? 0.5 : 1 }}>해제</button>
            <button type="button" onClick={bulkDeleteEditSelection} disabled={selectedEditCount === 0} style={{ ...smBtn('#dc2626'), display: 'inline-flex', alignItems: 'center', gap: '4px', opacity: selectedEditCount === 0 ? 0.5 : 1 }}><IconTrash size={14} />선택 삭제</button>
            {/* 클로바엔 시각 숫자칸이 없다 → 평소엔 숨기고 이 토글로만 드러낸다(클로바 묶음 D2c). */}
            <button type="button" onClick={() => setTimeEditOpen((on) => !on)} aria-pressed={timeEditOpen}
              style={{
                ...smBtn('#64635f'),
                marginLeft: 'auto',
                background: timeEditOpen ? T.color.selectedBg : T.color.surface,
                color: timeEditOpen ? T.color.selectedText : '#32312e',
                borderColor: timeEditOpen ? T.color.selectedText : T.color.outlineBtnBorder,
              }}>시각 고치기</button>
          </div>
          {deletedEditSegs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
              {deletedEditSegs.map(({ seg }, i) => (
                <div key={`deleted-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '7px 9px', border: '1px dashed #fecaca', borderRadius: '8px', background: '#fef2f2', color: '#991b1b' }}>
                  <span style={{ flexShrink: 0, minWidth: '54px', textAlign: 'right', fontSize: '12px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', textDecoration: 'line-through' }}>{editSecondText(seg.start)}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '13px', lineHeight: 1.45, whiteSpace: 'pre-wrap', textDecoration: 'line-through', ...softWrap }}>{seg.text || '(내용 없음)'}</span>
                  <button type="button" onClick={() => restoreEditSeg(i)} style={{ ...smBtn('#b91c1c'), flexShrink: 0, background: '#fff' }}>되돌리기</button>
                </div>
              ))}
            </div>
          )}
          {/* 제자리 편집판(클로바 묶음 D2c) — 읽기와 같은 화자 묶음 배치를 회색 판 안에 그린다.
              🔴 행·textarea 를 안쪽 컴포넌트로 빼지 않는다(인라인 JSX) — 렌더마다 새 타입이 되어
                 통째로 다시 마운트되고, 글자를 치는 중 포커스가 사라진다.
              🔴 textarea 높이는 **CSS 겹침**으로 늘린다(같은 글을 가진 보이지 않는 div 와 한 grid
                 칸에 겹친다) — ref/onInput 으로 scrollHeight 를 재면 렌더마다 강제 레이아웃이
                 걸려 긴 전사에서 입력이 눈에 보이게 느려진다(1,200발화 실측 234ms → 2,619ms). */}
          <div style={{ background: '#fbfbfa', borderRadius: '10px', padding: mobile ? '10px' : '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {editTranscriptGroups.map((group) => {
              const first = group.items[0].s;
              const speakerName = first.speaker ? editSpeakerName(first.speaker) : '화자 없음';
              return (
                <div key={`edit-group-${group.items[0].i}`} style={{ padding: '2px 0 0' }}>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#32312e', marginBottom: '8px', minWidth: 0, ...softWrap }}>{speakerName}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginLeft: mobile ? 0 : '36px' }}>
                    {group.items.map(({ s, i }) => {
                      const timingInvalid = editTimingInvalid(s);
                      // 잘못된 시각은 토글과 무관하게 숫자칸을 보인다 — 저장이 막히는 이유가 보이게.
                      const showTimeInputs = timeEditOpen || timingInvalid;
                      const timeInputStyle = { width: '78px', fontSize: '12px', padding: '3px 6px', borderRadius: '6px', border: `1px solid ${timingInvalid ? '#dc2626' : '#e5e5e2'}`, boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' };
                      // 겹치는 두 칸은 글꼴·줄높이·줄바꿈 규칙이 같아야 높이가 어긋나지 않는다.
                      const textCellStyle = {
                        gridArea: '1 / 1',
                        margin: 0,
                        padding: 0,
                        font: 'inherit',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      };
                      return (
                        <div key={i} data-testid={`edit-seg-${i}`}
                          style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap', padding: '4px 0' }}>
                          <input type="checkbox" checked={selectedEditSet.has(i)} onChange={() => toggleEditSelection(i)} aria-label={`${i + 1}번 세그먼트 선택`} style={{ marginTop: '6px', flexShrink: 0 }} />
                          <button type="button" aria-label={`${i + 1}번 발화 재생`} title="이 지점부터 재생" disabled={!audioUrl}
                            onClick={() => seekTo(Number(s.start) || 0)}
                            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', marginTop: '2px', padding: 0, border: 'none', background: 'transparent', borderRadius: '6px', color: audioUrl ? T.color.selectedText : '#92918d', cursor: audioUrl ? 'pointer' : 'default' }}>
                            <IconPlayerPlay size={14} />
                          </button>
                          {/* 시각 숫자칸(«시각 고치기» 를 켰거나 시각이 잘못된 행)은 시각 글자 자리에 들어간다.
                              🔴 숫자칸 묶음은 shrink 를 막지 않는다 — 아주 좁은 칸에서는 이 묶음 안에서
                                 다시 줄바꿈해야 가로로 넘치지 않는다. */}
                          {showTimeInputs ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', minWidth: 0 }}>
                              <span style={{ fontSize: '11px', color: '#64635f', fontWeight: 800 }}>시작</span>
                              <input type="number" min="0" step="0.01" value={s.start ?? ''} onChange={(e) => updateEditSeg(i, { start: e.target.value })} style={timeInputStyle} aria-label={`${i + 1}번 세그먼트 시작 초`} />
                              <span style={{ fontSize: '11px', color: '#64635f', fontWeight: 800 }}>종료</span>
                              <input type="number" min="0" step="0.01" value={s.end ?? ''} onChange={(e) => updateEditSeg(i, { end: e.target.value })} style={timeInputStyle} aria-label={`${i + 1}번 세그먼트 종료 초`} />
                            </div>
                          ) : (
                            <span style={{ flexShrink: 0, marginTop: '3px', minWidth: '38px', fontSize: '11px', fontWeight: 700, color: '#92918d', fontVariantNumeric: 'tabular-nums' }}>{editSecondText(s.start)}</span>
                          )}
                          <div style={{ display: 'grid', flex: mobile ? '1 1 100%' : '1 1 160px', order: mobile ? 2 : undefined, minWidth: 0 }}>
                            {/* 높이만 만드는 거울 칸 — 끝 공백 하나를 더해야 마지막 줄바꿈까지 따라간다. */}
                            <div aria-hidden="true" style={{ ...textCellStyle, visibility: 'hidden' }}>{`${s.text ?? ''} `}</div>
                            <textarea value={s.text ?? ''} onChange={(e) => updateEditSeg(i, { text: e.target.value })}
                              aria-label={`${i + 1}번 발화 내용`}
                              onFocus={(e) => { e.target.style.boxShadow = 'inset 0 -1px 0 #92918d'; }}
                              onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                              style={{ ...textCellStyle, width: '100%', height: '100%', overflow: 'hidden', border: 'none', outline: 'none', background: 'transparent', resize: 'none', color: '#32312e', boxSizing: 'border-box' }} />
                          </div>
                          <button type="button" onClick={() => markEditSegDeleted(i)} title="세그먼트 삭제" aria-label={`${i + 1}번 세그먼트 삭제`} style={{ ...iconButtonStyle('#dc2626'), width: '30px', height: '30px', flexShrink: 0, marginLeft: 'auto', background: 'transparent' }}><IconTrash size={16} /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {editSegs.length === 0 && (
              <div style={{ color: '#92918d', fontSize: '13px', textAlign: 'center', padding: '14px 0' }}>저장하려면 최소 1개 세그먼트를 되돌리십시오.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // 상세 — 목록에서 노트 선택 시 전체폭, '← 목록'으로 복귀
  return (
        <main className="gw-tabbar-pad" style={mobile
          ? { flex: 1, minWidth: 0, overflowY: 'auto', padding: '0 14px 14px', display: (active || adminView) ? 'block' : 'none', boxSizing: 'border-box' }
          : { flex: 1, minWidth: 0, overflowY: 'auto', padding: '0 24px 24px', boxSizing: 'border-box' }}>
          {(active || adminView) && (
            <button onClick={() => { setActive(null); setAdminView(false); }}
              style={{ position: 'sticky', top: 0, zIndex: 5, margin: '8px 0', background: '#f1f1ef', color: '#32312e', border: '1px solid #e5e5e2', borderRadius: '8px', padding: '7px 12px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>← 목록</button>
          )}
          {adminView ? (
            <div style={{ maxWidth: '960px', margin: mobile ? 0 : '0 auto' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '14px' }}>노트 관리</h2>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#32312e', margin: '10px 0 8px' }}>사용자별 STT 사용량</div>
              <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflowX: 'auto', marginBottom: '20px' }}>
                <table style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead><tr style={{ background: '#fbfbfa', textAlign: 'left' }}><th style={thAdmin}>사용자</th><th style={thAdmin}>노트</th><th style={thAdmin}>변환 시간</th></tr></thead>
                  <tbody>
                    {usage.map((u, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #eeeeec' }}><td style={tdAdmin}>{u.user}</td><td style={tdAdmin}>{u.meetings}건</td><td style={tdAdmin}>{u.minutes}분</td></tr>
                    ))}
                    {usage.length === 0 && <tr><td colSpan={3} style={{ ...tdAdmin, color: '#92918d', textAlign: 'center' }}>데이터 없음</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#32312e', margin: '10px 0 8px' }}>감사 로그 (최근 200)</div>
              <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '620px', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead><tr style={{ background: '#fbfbfa', textAlign: 'left' }}><th style={thAdmin}>시각</th><th style={thAdmin}>사용자</th><th style={thAdmin}>동작</th><th style={thAdmin}>상세</th></tr></thead>
                  <tbody>
                    {auditLog.map((a) => (
                      <tr key={a.id} style={{ borderTop: '1px solid #eeeeec' }}><td style={tdAdmin}>{fmtDate(a.created_at)}</td><td style={tdAdmin}>{a.user}</td><td style={tdAdmin}>{ACTIONS[a.action] || a.action}</td><td style={{ ...tdAdmin, color: '#64635f', ...softWrap }}>{a.detail}</td></tr>
                    ))}
                    {auditLog.length === 0 && <tr><td colSpan={4} style={{ ...tdAdmin, color: '#92918d', textAlign: 'center' }}>로그 없음</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          ) : !active ? (
            <div style={{ maxWidth: '520px', margin: '80px auto 0', padding: '22px', border: '1px dashed #e5e5e2', borderRadius: '8px', background: '#fff', color: '#92918d', textAlign: 'center', fontSize: '14px' }}>노트를 선택하면 전사, 요약, 녹음 메모를 확인할 수 있습니다.</div>
          ) : (
            // 전사 화면은 폭을 제한하지 않는다 — 목록이 접히면서 생긴 좌우 여백이 과했다(마스터 지시 2026-08-12).
            // 좌우 배분은 아래 2열의 드래그 구분선으로 사용자가 정한다.
            <div style={{ maxWidth: active.status === 'done' ? 'none' : '920px', margin: mobile ? 0 : '0 auto', paddingBottom: hasBottomAudio ? (mobile ? '96px' : '86px') : 0 }}>
              <div ref={stickyHeadRef} style={{ position: 'sticky', top: 0, zIndex: 5, background: T.color.page, paddingTop: '22px', marginBottom: '16px', boxShadow: '0 6px 8px -8px rgba(15,23,42,.16)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <h2 style={{ fontSize: mobile ? '20px' : '22px', fontWeight: 800, margin: 0, color: '#32312e', minWidth: 0, ...softWrap }}>{active.title}</h2>
                      {/* 제목 옆 ⌄ — 장식이 아니라 참석자 줄을 여닫는 단추다(클로바 묶음 C3a). */}
                      <button type="button" onClick={() => setPeopleOpen((v) => !v)} aria-label="참석자 보기" aria-expanded={peopleOpen}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92918d', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}>
                        {peopleOpen ? '⌃' : '⌄'}
                      </button>
                      <button onClick={() => renameMeeting(active)} title="이름 수정" aria-label="이름 수정" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64635f', padding: '2px', display: 'inline-flex' }}><IconPencil size={16} /></button>
                      {(() => { const st = STATUS[active.status] || STATUS.uploaded; return <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '999px', background: st.bg, color: st.color, fontWeight: 700 }}>{st.label}</span>; })()}
                    </div>
                    {(folderName || metaParts.length > 0) && (
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#64635f', lineHeight: 1.45, ...softWrap }}>
                        {folderName && (
                          <span data-testid="meeting-meta-folder" title={folderName}
                            style={{ display: 'inline-block', maxWidth: '100%', verticalAlign: 'bottom', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folderName}</span>
                        )}
                        {folderName && metaParts.length > 0 ? ' · ' : ''}
                        {metaParts.join(' · ')}
                      </div>
                    )}
                    {peopleOpen && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#64635f' }}>참석자</span>
                        {participants.map((name, k) => (
                          <span key={name} id={`mpeople-${k}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', padding: '3px 5px 3px 9px', borderRadius: T.radius.pill, border: `1px solid ${T.color.border}`, background: T.color.subtle, color: T.color.text, fontSize: T.font.sm, fontWeight: 700, ...jumpPulseStyle(`mpeople-${k}`, T.radius.pill) }}>
                            <span style={{ minWidth: 0, ...softWrap }}>{hasSearch ? renderTranscriptSearchText(name, searchNeedle) : name}</span>
                            <button type="button" onClick={() => removeParticipant(name)} disabled={participantSaving}
                              title={`참석자 삭제 ${name}`} aria-label={`참석자 삭제 ${name}`}
                              style={{ width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: T.color.faint, cursor: participantSaving ? 'not-allowed' : 'pointer', fontSize: '14px', lineHeight: 1 }}>
                              ×
                            </button>
                          </span>
                        ))}
                        {participantInputOpen ? (
                          <input autoFocus aria-label="참석자 이름" placeholder="이름 입력 후 Enter" maxLength={PARTICIPANT_NAME_MAX}
                            onKeyDown={participantInputKeyDown}
                            style={{ width: '132px', fontSize: T.font.sm, padding: '4px 8px', borderRadius: T.radius.md, border: `1px solid ${T.color.border}`, boxSizing: 'border-box' }} />
                        ) : (
                          <button type="button" onClick={() => setParticipantInputOpen(true)} aria-label="참석자 추가"
                            disabled={participantSaving || participants.length >= PARTICIPANTS_MAX}
                            style={{ fontSize: T.font.sm, fontWeight: 700, padding: '3px 9px', borderRadius: T.radius.pill, border: `1px dashed ${T.color.outlineBtnBorder}`, background: '#fff', color: T.color.muted, cursor: (participantSaving || participants.length >= PARTICIPANTS_MAX) ? 'not-allowed' : 'pointer', opacity: (participantSaving || participants.length >= PARTICIPANTS_MAX) ? 0.55 : 1 }}>
                            참석자 ⊕
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* 검색(클로바 묶음 D2b) — 머리 아래 전체 폭 검색 줄을 연다.
                        자리는 지도의 «검색 · 다운로드 · 모아보기 · ⋮» 순서라 «다운로드» 바로 앞이다.
                        🔴 변환 완료(done)일 때만 둔다 — 요약·메모·전사가 done 블록 안에서만 그려져
                           그 전에는 이동할 대상 DOM 이 아예 없다. 편집 중엔 숨긴다(모아보기와 같다).
                        🔴 접근 이름 «검색» 은 사이드바 전역 검색 단추와 같다 — e2e 는 data-testid 로 잡는다. */}
                    {active.status === 'done' && !editing && (
                      <button type="button" onClick={toggleSearch}
                        title="검색" aria-label="검색" aria-pressed={searchOpen} data-testid="search-open"
                        style={{ ...iconButtonStyle(searchOpen ? T.color.selectedText : '#64635f'), background: searchOpen ? T.color.selectedBg : '#fff' }}>
                        <IconSearch size={18} />
                      </button>
                    )}
                    {/* 받을 파일은 선 아이콘 단추가 아니라 «다운로드 ▾» 메뉴 하나에 모은다(클로바 묶음 C2, 2026-10-04).
                        🔴 항목의 aria-label(오디오 다운로드·TXT 다운로드)과 표시 조건은 그대로 둔다 —
                        e2e(test_meetings_full.py B-3)가 그 이름의 button 개수로 노트 상태를 추론한다. */}
                    <details style={{ position: 'relative' }} onToggle={closeOtherMenus}>
                      <summary title="다운로드" aria-label="다운로드" style={{ ...iconButtonStyle('#64635f'), listStyle: 'none' }}><IconDownload size={18} /></summary>
                      <div style={{ position: 'absolute', right: 0, top: '40px', zIndex: 20, minWidth: '178px', padding: '5px', border: '1px solid #e5e5e2', borderRadius: '8px', background: '#fff', boxShadow: '0 10px 24px rgba(15,23,42,.14)' }}>
                        {active.status === 'done' && (
                          <button onClick={(e) => { closeDetailMenu(e); dlTxt(active); }} aria-label="TXT 다운로드" style={menuItemStyle}><IconFileText size={16} />음성 기록</button>
                        )}
                        {active.has_audio && (
                          <button onClick={(e) => { closeDetailMenu(e); dlAudio(active); }} aria-label="오디오 다운로드" style={menuItemStyle}><IconDownload size={16} />음성 파일</button>
                        )}
                        {/* 🔴 «요약·메모 다운로드»(TXT) aria-label 과 인자 하나 호출은 그대로 — 시험·e2e(B-3·B-18)가 그 이름과
                            파일명 `_요약_메모.txt` 에 기댄다. MD·DOCX 는 같은 조건으로 그 아래에 더한다(2026-10-15). */}
                        {hasSummaryOrNotes(active) && dlSummaryNotes && (
                          <button onClick={(e) => { closeDetailMenu(e); dlSummaryNotes(active); }} aria-label="요약·메모 다운로드" style={menuItemStyle}><IconFileText size={16} />요약·메모 (TXT)</button>
                        )}
                        {hasSummaryOrNotes(active) && dlSummaryNotes && (
                          <button onClick={(e) => { closeDetailMenu(e); dlSummaryNotes(active, 'md'); }} aria-label="요약·메모 MD 다운로드" style={menuItemStyle}><IconFileText size={16} />요약·메모 (MD)</button>
                        )}
                        {hasSummaryOrNotes(active) && dlSummaryNotes && (
                          <button onClick={(e) => { closeDetailMenu(e); dlSummaryNotes(active, 'docx'); }} aria-label="요약·메모 DOCX 다운로드" style={menuItemStyle}><IconFileText size={16} />요약·메모 (DOCX)</button>
                        )}
                        {!(active.status === 'done' || active.has_audio || (hasSummaryOrNotes(active) && dlSummaryNotes)) && (
                          <div style={{ padding: '8px 10px', fontSize: '13px', color: '#92918d' }}>받을 파일이 아직 없습니다.</div>
                        )}
                      </div>
                    </details>
                    {/* 모아보기(클로바 묶음 D2a) — 오른쪽 패널을 북마크·하이라이트 모음으로 바꾼다.
                        자리는 지도의 «검색 · 다운로드 · 모아보기 · ⋮» 순서다.
                        🔴 편집 중엔 숨긴다(클로바와 같다) — 판도 함께 닫힌다(위 effect). */}
                    {active.status === 'done' && !editing && (
                      <button type="button" onClick={() => (sideTab === 'collect' ? closeCollect() : openCollect())}
                        title="모아보기" aria-label="모아보기" aria-pressed={sideTab === 'collect'} data-testid="collect-open"
                        style={{ ...iconButtonStyle(sideTab === 'collect' ? T.color.selectedText : '#64635f'), background: sideTab === 'collect' ? T.color.selectedBg : '#fff' }}>
                        <IconStack2 size={18} />
                      </button>
                    )}
                    {/* 변환 시작/다시 변환·화자분리 다시 실행·RAG 등록은 도구줄이 아니라 ⋮ 메뉴 안에 둔다(마스터 지시 2026-10-02). */}
                    <details style={{ position: 'relative' }} onToggle={closeOtherMenus}>
                      <summary title="더보기" aria-label="더보기" style={{ ...iconButtonStyle('#64635f'), listStyle: 'none' }}><IconDotsVertical size={18} /></summary>
                      <div style={{ position: 'absolute', right: 0, top: '40px', zIndex: 20, minWidth: '178px', padding: '5px', border: '1px solid #e5e5e2', borderRadius: '8px', background: '#fff', boxShadow: '0 10px 24px rgba(15,23,42,.14)' }}>
                        {/* 위 묶음 = 클로바 캡처 13 의 순서(단어 바꾸기 · 노트 이동 · 휴지통으로 이동).
                            🔸 편집 권한은 `_meeting_dict` 에 없어 읽기 공유 사용자에게도 보인다 —
                               누르면 서버 403 «편집 권한이 없습니다.» 가 뜬다(이동·삭제와 같은 방식). */}
                        {active.status === 'done' && !editing && !summarizing && !chaptersBusy && (
                          <button onClick={(e) => { closeDetailMenu(e); setReplaceInit(REPLACE_INIT_ALL); setReplaceOpen(true); }} style={menuItemStyle}><IconReplace size={16} />단어 바꾸기</button>
                        )}
                        <button onClick={(e) => { closeDetailMenu(e); setMoveOpen(true); }} style={menuItemStyle}><IconFolder size={16} />노트 이동</button>
                        <button onClick={(e) => { closeDetailMenu(e); delMeeting(active); }} style={{ ...menuItemStyle, color: '#dc2626' }}><IconTrash size={16} />휴지통으로 이동</button>
                        {(active.status === 'ready' || active.status === 'failed' || active.status === 'cancelled') && active.has_audio && (
                          <button onClick={(e) => { closeDetailMenu(e); startStt(active); }} disabled={sttStarting}
                            style={{ ...menuItemStyle, cursor: sttStarting ? 'not-allowed' : 'pointer', opacity: sttStarting ? 0.6 : 1 }}>
                            <IconPlayerPlay size={16} />{active.status === 'ready' ? '변환 시작' : '다시 변환'}
                          </button>
                        )}
                        {/* 변환 중지(C3c · 마스터 «12 되살려») — 확인 창은 부모 cancelStt 가 상태별로 묻는다. */}
                        {CANCELLABLE_STT_STATUSES.includes(active.status) && cancelStt && (
                          <button onClick={(e) => { closeDetailMenu(e); cancelStt(active); }} style={menuItemStyle}>
                            <IconX size={16} />{active.status === 'rediarize' ? '화자분리 중지' : '변환 중지'}
                          </button>
                        )}
                        {active.status === 'done' && active.has_audio && requestRediarize && (
                          <button onClick={(e) => { closeDetailMenu(e); requestRediarize(active); }} disabled={rediarizing}
                            style={{ ...menuItemStyle, cursor: rediarizing ? 'not-allowed' : 'pointer', opacity: rediarizing ? 0.6 : 1 }}>
                            <IconUsers size={16} />{rediarizing ? '화자분리 요청 중' : '화자분리 다시 실행'}
                          </button>
                        )}
                        {active.status === 'done' && setRagOpt && (
                          <button onClick={(e) => { closeDetailMenu(e); handleRagOpt(); }} title={active.rag_opt_in ? 'RAG 등록됨 — 클릭 시 해제' : 'RAG 등록'}
                            style={{ ...menuItemStyle, color: active.rag_opt_in ? T.color.selectedText : '#32312e' }}>
                            <IconBrain size={16} />{active.rag_opt_in ? 'RAG 등록됨' : 'RAG 등록'}
                          </button>
                        )}
                        <button onClick={(e) => { closeDetailMenu(e); setGlossaryOpen(true); }} style={menuItemStyle}><IconBook size={16} />단어장</button>
                        {/* 아래 묶음 = 보기 설정(클로바 캡처 13). 위 묶음의 이름·순서·조건은 건드리지 않는다. */}
                        <div role="separator" style={{ height: '1px', margin: '5px 2px', background: '#e5e5e2' }} />
                        <button onClick={(e) => { closeDetailMenu(e); setHideSpeakers((v) => !v); }} style={menuItemStyle}>
                          <IconUsers size={16} />{hideSpeakers ? '참석자 보이기' : '참석자 숨기기'}
                        </button>
                        {active.status === 'done' && transcript.length > 0 && (
                          <button onClick={(e) => { closeDetailMenu(e); setShareOpen(true); }} style={menuItemStyle}>
                            <IconUsers size={16} />참석자 대화 점유율
                          </button>
                        )}
                      </div>
                    </details>
                  </div>
                </div>
                {/* 머리 아래 전체 폭 검색 줄(클로바 묶음 D2b) — 제목·아이콘 가로 줄 **다음**에 둔다.
                    참석자 줄 뒤(왼쪽 열 안)에 두면 전체 폭이 아니다.
                    🔴 안쪽 컴포넌트로 빼지 않고 인라인 JSX 로 그린다 — 렌더마다 새 타입이 되면
                       글자를 칠 때마다 입력칸이 다시 마운트되어 포커스·한글 조합이 깨진다.
                    🔴 위 가로 줄에 이미 아래 여백 10 이 있어 marginTop 은 두지 않는다. */}
                {searchOpen && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', paddingBottom: '10px' }}>
                    <label style={{ position: 'relative', flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center' }}>
                      <IconSearch size={16} style={{ position: 'absolute', left: '10px', color: '#92918d', pointerEvents: 'none' }} />
                      <input autoFocus value={transcriptSearch} onChange={handleSearchChange} onKeyDown={handleSearchKeyDown}
                        placeholder="음성 기록·요약·메모·참석자 검색" aria-label="노트 검색"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e5e2', borderRadius: '8px', padding: '8px 34px 8px 32px', fontSize: '13px', color: '#32312e', outline: 'none', background: '#fff' }} />
                      {transcriptSearch && (
                        <button type="button" onClick={clearSearchText} title="검색어 지우기" aria-label="검색어 지우기"
                          style={{ position: 'absolute', right: '7px', width: '22px', height: '22px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: '#92918d', cursor: 'pointer', padding: 0 }}>
                          <IconX size={15} />
                        </button>
                      )}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
                      <span style={{ minWidth: '42px', textAlign: 'center', fontSize: '12px', fontWeight: 800, color: hasSearch ? '#64635f' : '#92918d', fontVariantNumeric: 'tabular-nums' }}>
                        {searchTotal > 0 ? `${safeSearchCursor + 1}/${searchTotal}` : '0/0'}
                      </span>
                      <button type="button" onClick={() => moveSearch(-1)} disabled={searchTotal <= 0} title="이전 검색 결과" aria-label="이전 검색 결과"
                        style={{ ...iconButtonStyle(searchTotal > 0 ? '#64635f' : '#92918d'), width: '30px', height: '30px', cursor: searchTotal > 0 ? 'pointer' : 'default', opacity: searchTotal > 0 ? 1 : 0.6 }}>
                        <IconChevronLeft size={16} />
                      </button>
                      <button type="button" onClick={() => moveSearch(1)} disabled={searchTotal <= 0} title="다음 검색 결과" aria-label="다음 검색 결과"
                        style={{ ...iconButtonStyle(searchTotal > 0 ? '#64635f' : '#92918d'), width: '30px', height: '30px', cursor: searchTotal > 0 ? 'pointer' : 'default', opacity: searchTotal > 0 ? 1 : 0.6 }}>
                        <IconChevronRight size={16} />
                      </button>
                      {/* 🔴 접근 이름은 ⋮ 의 «단어 바꾸기» 와 겹치지 않게 다르게 둔다(보이는 글자는 같다). */}
                      {canReplaceFromSearch && (
                        <button type="button" onClick={openReplaceFromSearch} aria-label="검색어로 단어 바꾸기" title="검색어로 단어 바꾸기"
                          style={{ height: '30px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e5e2', background: '#fff', color: '#64635f', fontSize: '12px', fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          단어 바꾸기
                        </button>
                      )}
                      <button type="button" onClick={resetSearch} title="닫기" aria-label="검색 닫기"
                        style={{ ...iconButtonStyle('#64635f'), width: '30px', height: '30px' }}>
                        <IconX size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {active.status === 'ready' && <div style={statusBox('#32312e', '#fbfbfa', '#e5e5e2')}>오디오가 준비됐습니다. ⋮ 메뉴의 <b>변환 시작</b>을 누르면 변환(전사)이 시작됩니다.</div>}
              {active.status === 'processing' && <div style={statusBox('#9a3412', '#fff7ed', '#fed7aa')}>{(active.progress || 0) >= 99 ? `화자 분석 중입니다… ${diarInfo(active, nowMs)}` : `변환 중입니다… ${procInfo(active, nowMs)}`} <span style={{ color: '#92918d' }}>(자동 새로고침)</span>{cancelHint}</div>}
              {(active.status === 'diarizing' || active.status === 'rediarize') && <div style={statusBox('#0369a1', '#e0f2fe', '#bae6fd')}>화자 분석 중입니다… {diarInfo(active, nowMs)} <span style={{ color: '#64635f' }}>(자동 새로고침)</span>{cancelHint}</div>}
              {active.status === 'uploaded' && <div style={statusBox('#32312e', '#fbfbfa', '#e5e5e2')}>변환 대기 중입니다…{queueText(active) ? ` (${queueText(active)})` : ''}{cancelHint}</div>}
              {active.status === 'failed' && <div style={statusBox('#991b1b', '#fef2f2', '#fecaca')}>변환 실패: {active.fail_reason} <span style={{ color: '#64635f' }}>⋮ 메뉴의 다시 변환으로 재시도할 수 있습니다.</span></div>}
              {active.status === 'cancelling' && <div style={statusBox('#9a3412', '#fff7ed', '#fed7aa')}>전사를 중지하는 중입니다… <span style={{ color: '#92918d' }}>(자동 새로고침)</span></div>}
              {active.status === 'cancelled' && <div style={statusBox('#32312e', '#fbfbfa', '#e5e5e2')}>전사가 중지되었습니다. ⋮ 메뉴의 <b>다시 변환</b>으로 다시 시작할 수 있습니다.</div>}
              {active.status === 'done' && (
                <div
                  ref={setSplitNode}
                  onMouseEnter={stacked ? undefined : () => setSplitAreaHover(true)}
                  onMouseLeave={stacked ? undefined : () => setSplitAreaHover(false)}
                  style={{
                    display: stacked ? 'flex' : 'grid',
                    flexDirection: stacked ? 'column' : undefined,
                    // 가운데 16px(SPLIT_GAP) = 옛 gap 자리. 그 안에 드래그 구분선을 그린다.
                    gridTemplateColumns: stacked ? undefined : `minmax(0, 1fr) ${SPLIT_GAP}px ${notesWShown}px`,
                    gap: stacked ? '16px' : 0,
                    alignItems: 'start',
                  }}>
                  <section style={{ order: stacked ? 2 : 1, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                    {keywords.length > 0 && (
                      <div style={cardStyle}>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#32312e', marginBottom: '10px' }}>주요 키워드</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {keywords.map((kw, i) => (
                            <span key={`${kw}-${i}`} style={{ fontSize: '12px', fontWeight: 800, padding: '5px 10px', borderRadius: '999px', background: T.color.surface, color: '#2383e2', border: '1px solid #e5e5e2', maxWidth: '100%', ...softWrap }}>{kw}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {TranscriptPanel()}
                  </section>
                  {!stacked && (
                    <div
                      ref={sepRef}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label="음성 기록과 메모·요약 폭 조절"
                      onMouseDown={startSplitDrag}
                      onMouseEnter={() => setSplitHandleHot(true)}
                      onMouseLeave={() => { if (!draggingRef.current) setSplitHandleHot(false); }}
                      onDoubleClick={() => setNotesW(clampNotesW(NOTES_DEFAULT))}
                      title="드래그해서 폭 조절 (더블클릭: 기본값)"
                      style={{ order: 2, alignSelf: 'stretch', display: 'flex', justifyContent: 'center', cursor: 'col-resize' }}>
                      <div style={{
                        width: '4px',
                        minHeight: '120px',
                        borderRadius: '999px',
                        background: splitHandleHot ? '#2383e2' : (splitAreaHover ? '#92918d' : 'transparent'),
                        transition: 'background .12s ease',
                      }} />
                    </div>
                  )}
                  {/* 🔴 모아보기 판의 sticky 는 판 상자가 아니라 **그리드 항목인 <aside>** 에 건다 —
                      그리드가 alignItems: start 라 aside 높이가 판과 같아서, 판에 걸면 움직일 자리가
                      없어 전사를 내리면 판이 화면 밖으로 밀려난다(크로미움 실측). */}
                  <aside style={{
                    order: stacked ? 1 : 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    minWidth: 0,
                    ...(sideTab === 'collect' && !stacked
                      ? { position: 'sticky', top: `${collectHeadH + COLLECT_GAP}px` }
                      : null),
                  }}>
                    {/* 🔴 모아보기가 열리면 요약·메모 tablist 와 패널은 아예 그리지 않는다 —
                        tablist 만 바꾸면 'collect' 일 때 메모 패널이 같이 그려진다. */}
                    {sideTab === 'collect' ? CollectCard() : (
                    <>
                    {/* 🔴 tablist 이름에 «요약 양식» 을 넣지 않는다 — e2e 가 그 이름으로 요약 양식 탭을 찾는다. */}
                    <div role="tablist" aria-label="요약과 메모"
                      style={{ display: 'inline-flex', alignSelf: 'flex-start', maxWidth: '100%', padding: '3px', borderRadius: T.radius.md, background: T.color.subtle, border: `1px solid ${T.color.border}`, gap: '3px' }}>
                      {[
                        { key: 'summary', label: '요약', count: null },
                        { key: 'memo', label: '메모', count: memoTotal },
                      ].map((t) => {
                        const on = sideTab === t.key;
                        return (
                          <button key={t.key} type="button" role="tab" id={`side-tab-${t.key}`}
                            aria-controls={`side-panel-${t.key}`} aria-selected={on}
                            onClick={() => setSideTab(t.key)}
                            style={{ border: 'none', borderRadius: T.radius.sm, background: on ? T.color.surface : 'transparent', color: on ? T.color.selectedText : T.color.muted, fontSize: T.font.sm, fontWeight: 800, padding: '6px 12px', cursor: 'pointer', boxShadow: on ? T.shadow.card : 'none' }}>
                            {t.label}
                            {/* 개수는 눈으로만 — 접근 이름은 정확히 «메모» 로 남긴다. */}
                            {t.count !== null && <span aria-hidden="true"> {t.count}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {/* 🔴 고른 탭의 패널 하나만 그린다 — 둘 다 그려 숨기면 같은 글이 화면에 둘 있게 된다. */}
                    {sideTab === 'summary' ? (
                      <div role="tabpanel" id="side-panel-summary" aria-labelledby="side-tab-summary" data-testid="side-panel-summary">
                        {SummaryCard()}
                      </div>
                    ) : (
                      <div role="tabpanel" id="side-panel-memo" aria-labelledby="side-tab-memo" data-testid="side-panel-memo"
                        style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
                        {canEditNotes && MemoAddBox()}
                        {RecordingNotesCard()}
                        {SegMemosCard()}
                      </div>
                    )}
                    </>
                    )}
                  </aside>
                </div>
              )}

              {hasBottomAudio && audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onLoadedMetadata={(e) => { e.currentTarget.playbackRate = playbackRate; }}
                  onTimeUpdate={(e) => setPlaySec(e.currentTarget.currentTime)}
                  onPlay={() => setAudioPlaying(true)}
                  onPause={() => setAudioPlaying(false)}
                  onEnded={() => setAudioPlaying(false)}
                  style={{ display: 'none' }}
                />
              )}
              {hasBottomAudio && (
                <div ref={playbarRef} style={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: mobile ? tabbarCalc() : 0,
                  zIndex: 999,
                  background: '#fff',
                  borderTop: '1px solid #e5e5e2',
                  boxShadow: '0 -8px 24px rgba(15,23,42,.12)',
                  padding: mobile ? '8px 12px' : '10px 24px',
                  boxSizing: 'border-box',
                }}>
                  {/* 🔴 오디오를 못 받았으면 못 쓰는 컨트롤 대신 사유 한 줄을 둔다 — 안 그러면 «불러오는 중»이
                      영구히 남는다. 'gone'(410) 은 다시 받아도 같으니 [다시 시도] 를 주지 않는다. */}
                  {audioError ? (
                    <div role="alert" style={{ maxWidth: '1180px', margin: '0 auto', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#b91c1c' }}>
                        {audioError === 'gone' ? '음성 파일이 서버에 없어 재생할 수 없습니다.' : '음성 파일을 불러오지 못했습니다.'}
                      </span>
                      {audioError !== 'gone' && (
                        <button type="button" onClick={() => retryAudio?.()} style={smBtn(T.color.slate)}>다시 시도</button>
                      )}
                    </div>
                  ) : (
                  <div style={{ maxWidth: '1180px', margin: '0 auto', display: 'grid', gridTemplateColumns: mobile ? '1fr' : '80px minmax(160px, 1fr) auto 80px', gap: mobile ? '8px' : '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#64635f', fontVariantNumeric: 'tabular-nums', textAlign: mobile ? 'left' : 'right' }}>{audioLoading ? <span style={{ fontSize: '11px', fontWeight: 700, color: '#92918d' }}>불러오는 중</span> : timeText(playSec)}</span>
                    <input
                      type="range"
                      min="0"
                      max={rangeMax}
                      value={rangeValue}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        if (audioRef.current) audioRef.current.currentTime = next;
                        setPlaySec(next);
                      }}
                      disabled={audioLoading}
                      style={{ width: '100%', accentColor: '#222', opacity: audioLoading ? 0.5 : 1 }}
                      aria-label="재생 위치"
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <select value={playbackRate} onChange={(e) => changeRate(Number(e.target.value))} aria-label="배속"
                        style={{ height: '32px', border: '1px solid #e5e5e2', borderRadius: '8px', background: '#fff', color: '#32312e', fontSize: '12px', fontWeight: 800, padding: '0 7px' }}>
                        <option value={1}>1x</option>
                        <option value={1.25}>1.25x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2}>2x</option>
                      </select>
                      <button type="button" onClick={() => skipAudio(-5)} title="5초 뒤로" style={{ ...iconButtonStyle('#64635f'), gap: '1px', width: '38px' }}><IconChevronLeft size={15} /><span style={{ fontSize: '11px', fontWeight: 900 }}>5</span></button>
                      <button type="button" onClick={toggleAudio} disabled={audioLoading} title={audioLoading ? '오디오 불러오는 중…' : (audioPlaying ? '일시정지' : '재생')} style={{ ...iconButtonStyle('#32312e'), width: '40px', height: '40px', border: 'none', background: 'transparent', opacity: audioLoading ? 0.45 : 1, cursor: audioLoading ? 'default' : 'pointer' }}>
                        {audioPlaying ? <IconPlayerPause size={21} /> : <IconPlayerPlay size={21} />}
                      </button>
                      <button type="button" onClick={() => skipAudio(5)} title="5초 앞으로" style={{ ...iconButtonStyle('#64635f'), gap: '1px', width: '38px' }}><span style={{ fontSize: '11px', fontWeight: 900 }}>5</span><IconChevronRight size={15} /></button>
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#64635f', fontVariantNumeric: 'tabular-nums', textAlign: mobile ? 'right' : 'left' }}>{timeText(totalDuration)}</span>
                  </div>
                  )}
                </div>
              )}
              {/* 참석자 대화 점유율 — 🔴 머리(sticky·zIndex 5) 안에 두면 쌓임 맥락에 갇혀 하단 재생바
                  (fixed·zIndex 999)에 가린다. 재생바와 같은 층에 두고 겹침은 1000 이상으로 올린다. */}
              {shareOpen && (
                <div onClick={() => setShareOpen(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                  <div role="dialog" aria-modal="true" aria-label="참석자 대화 점유율" onClick={(e) => e.stopPropagation()}
                    style={{ width: '100%', maxWidth: '420px', background: '#fff', borderRadius: T.radius.lg, boxShadow: T.shadow.raised, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 12px', borderBottom: `1px solid ${T.color.border}`, background: T.color.subtle }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: T.font.lg, fontWeight: 900, color: T.color.textStrong }}>참석자 대화 점유율</div>
                      <button type="button" title="닫기" aria-label="닫기" onClick={() => setShareOpen(false)}
                        style={{ border: 'none', background: 'transparent', padding: '2px', color: T.color.muted, cursor: 'pointer', display: 'inline-flex' }}>
                        <IconX size={16} />
                      </button>
                    </div>
                    <div style={{ maxHeight: '320px', overflowY: 'auto', padding: '12px' }}>
                      {(() => {
                        const rows = speakerShare(transcript);
                        if (rows.length === 0) {
                          return <div style={{ fontSize: T.font.base, color: T.color.faint }}>말한 시간을 계산할 수 없습니다.</div>;
                        }
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {rows.map((row, i) => {
                              const pct = Math.round(row.ratio * 100);
                              return (
                                <div key={`share-${row.speaker ?? '__no_speaker__'}-${i}`}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ flex: 1, minWidth: 0, fontSize: T.font.base, fontWeight: 800, color: T.color.textStrong, ...softWrap }}>
                                      {row.speaker ? dispSpk(row.speaker, spkDisp) : '화자 없음'}
                                    </span>
                                    <span style={{ fontSize: T.font.sm, fontWeight: 700, color: T.color.muted, fontVariantNumeric: 'tabular-nums' }}>{shareTimeText(row.seconds)}</span>
                                    <span style={{ minWidth: '36px', textAlign: 'right', fontSize: T.font.sm, fontWeight: 900, color: T.color.textStrong, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                                  </div>
                                  <div style={{ height: '6px', borderRadius: T.radius.pill, background: T.color.subtle, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: T.radius.pill, background: speakerAvatar(row.speaker, spkDisp).color }} />
                                  </div>
                                </div>
                              );
                            })}
                            <div style={{ fontSize: T.font.xs, color: T.color.faint, lineHeight: 1.5 }}>
                              말한 시간의 합을 100% 로 본 비율입니다(반올림해 합이 100% 가 아닐 수 있습니다).
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
              {/* 단어 바꾸기·단어장 창도 점유율 창과 같은 층(머리 밖)에 둔다 — 머리 안에 두면
                  sticky 쌓임 맥락에 갇혀 하단 재생바에 가린다. */}
              {replaceOpen && (
                <WordReplaceDialog
                  meeting={active}
                  initialFind={replaceInit.find}
                  initialTargets={replaceInit.targets}
                  onClose={() => setReplaceOpen(false)}
                  onReplaced={(next) => {
                    const noteId = active.id;
                    // 요약 본문이 바뀌면 이전 반증 ⚠ 한 줄은 맞지 않다(서버 ⚠ 캐시도 함께 지워진다).
                    // 챕터가 바뀌어도 같다(F2c-2a — 챕터 ⚠ 는 서버에 남지 않는다).
                    const has = (k) => !!next && Object.prototype.hasOwnProperty.call(next, k);
                    const patch = has('summary') || has('chapters')
                      ? {
                        ...next,
                        ...(has('summary') ? { summary_falsify_warn: '' } : {}),
                        ...(has('chapters') ? { chapters_falsify_warn: '' } : {}),
                      }
                      : next;
                    setActive((a) => (a && a.id === noteId ? { ...a, ...patch } : a));
                  }}
                />
              )}
              {glossaryOpen && <GlossaryDialog onClose={() => setGlossaryOpen(false)} />}
            </div>
          )}
        </main>
  );
}
